// src/chat/index.js — 💬 Messages (Phase A3): the staff-app chat UI.
//
// The WhatsApp replacement, deliberately skinned like WhatsApp — and specifically
// like Matt's ELC Jack assistant (mattkiernan86-byte/Jack index.html), so staff
// land somewhere that already feels familiar: #075e54 teal header, #25d366 green
// accents, white bubbles for others / #d9fdd3 for me (corner-tail via a squared
// top radius), #eae6df chat wallpaper with the faint dot pattern, #f0f2f5
// composer bar with a round green send button, white date chips.
//
// Identity model (matches the data plane, A2):
//   - The till user PICKS THEMSELVES once (stored in localStorage: id + name
//     ONLY). Reads + unread cursors ride on that pick.
//   - SENDING requires their PIN (kept IN MEMORY ONLY, wiped after 5 idle
//     minutes or a switch — NEVER localStorage). The Worker attributes the
//     message from the verified staff row, so a message can't be sent as
//     someone else without their PIN.
//   - "🔒 Visible to Head Office" is shown on every thread — the transparency
//     that makes the WhatsApp ban workable is stated, not hidden.
//
// Polling: 15s while the Chat tab is open and the page is visible (Cloudflare
// paid plan; the plan doc caps cadence deliberately). Bare-name globals from
// index.html (currentScreen, currentStore, getStoreStaff) are guarded with
// typeof — same classic-script contract as every extracted bundle.

// v129.19 — the recipient ceiling is a SEND-PATH rule; the picker imports the
// same one rather than hard-coding a second copy that could drift.
import { addGroupToRecipients, replyAllRecipients, hasMoreThanSender } from '../shared/email-recipients.js';
// v129.44 (Dubai Hills) — sorting the inbox and sent items. Pure + shared so
// the rule is testable without a browser; no API call, the list is in memory.
import { sortEmails, sortNote, whoLabel } from '../shared/email-sort.js';
import { buildReplyQuote } from '../shared/email-reply-quote.js';   // v136.29 - the earlier email below a reply
import { filterEmails, searchNote } from '../shared/email-search.js';
import { decodeDataUrl, openStoredFile } from '../shared/open-file.js';   // v129.119 — voice-note playback via blob (the v129.112 rule: never a raw data: sink)
// v129.121 — the recorder MOVED to a shared module: Head Office chat is a
// separate bundle and v129.119 only reached this one. One copy now.
import { startRecording, stopRecording, cancelRecording, isRecording, playVoiceInto, voiceStubHtml, fmtDur as vnFmtDur, voiceSupported } from '../shared/voice-note.js';
// v136.8 (accessibility audit): the Shift are-you-sure box, imported here too so this lazily loaded bundle never
// depends on another bundle having set window.uiConfirm first (window.uiConfirm wins when the page already has it);
// and Enter/Space on the list rows, which are role="button" divs.
import { uiConfirm as sharedConfirm } from '../shared/confirm-dialog.js';
import '../shared/key-activate.js';
function tr(s) { return (typeof I18N !== 'undefined' && I18N.t) ? I18N.t(s) : s; }
function askConfirm(msg, opts) {
  var f = (typeof window !== 'undefined' && typeof window.uiConfirm === 'function') ? window.uiConfirm : sharedConfirm;
  return f(msg, opts);
}

function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
// v132.85 (overnight audit): the ONE safe way to put text inside
// a quoted inline-handler argument. JS-escape first (backslash, quote, new line), THEN
// HTML-escape. esc() alone turns ' into &#39;, which the browser decodes
// back to a quote before the handler runs, so a crafted name broke out.
function jsArg(s) {
  return esc(String(s == null ? '' : s).replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\r?\n/g, '\\n'));
}

// SECURITY (v125.14): a photo src is ALWAYS a base64 image data-URL. Anything
// else — including a payload smuggled past an old build — is blanked before it
// reaches innerHTML, so the photo bubbles can never inject markup. The server
// validator (validatePhoto) is the primary fix; this is belt-and-braces that
// also neutralises anything already stored.
function okPhoto(s) {
  return /^data:image\/(jpeg|jpg|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/.test(String(s == null ? '' : s)) ? String(s) : '';
}

function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  // A6-4: an AM/HO logged into the staff app carries a Bearer session — attach it
  // so the whole chat runs on their ACCOUNT identity (no who-picker, no PIN).
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}

// A6-4: is the staff app being used by an ACCOUNT (AM / HO / ho_staff)? Their
// password login already proves identity — chat skips the picker and the PIN.
function isAccountMode() {
  try {
    return !!(typeof currentUser !== 'undefined' && currentUser &&
      ['admin', 'area_manager', 'ho_staff'].indexOf(String(currentUser.role)) !== -1 &&
      typeof DB !== 'undefined' && DB.getToken && DB.getToken());
  } catch (e) { return false; }
}
var _acctName = null;   // viewer name from the channels response (my-bubble detection)
var _commsTab = 'chat'; // v125.19: the Comms hub sub-tab — 'chat' | 'email'

// ── 📧 Store Email state (v125.37, Store Email PR2) ─────────────────────────
// The inbox rides the same identity as chat: staff reads carry the picked
// _who's staff_id (the Worker derives the store server-side), accounts ride
// their Bearer + the current store. The API never returns an HTML body
// (v125.36 security pin) — everything rendered here is esc()'d text.
var _emails = null;      // null = not loaded yet; [] = loaded, empty
var _emailsHasMore = false; // v127.35 — a full page (50) means older mail exists; drives the Load-older row
var _emailFolder = 'in'; // v126.79 — '📥 Inbox' | '↗️ Sent' view (Doha - Landmark Mall request)
// v129.44 — sort order, remembered per DEVICE. A till is one device and the
// person who prefers by-sender wants it that way tomorrow too. Date-desc is
// the default and is EXACTLY what the inbox did before sorting existed.
var _emailSort = { key: 'date', dir: 'desc' };
try {
  var _es = JSON.parse(localStorage.getItem('elcEmailSort') || 'null');
  if (_es && _es.key) _emailSort = { key: _es.key, dir: _es.dir === 'asc' ? 'asc' : 'desc' };
} catch (e) { /* private mode / junk value — keep the default */ }
// v129.106 (Dubai Hills) — the mail search text. DELIBERATELY not persisted,
// unlike _emailSort: an order you left set is still showing you every email
// tomorrow, whereas a query you left set is HIDING most of them. A till that
// opens on a filtered inbox looks like a broken inbox.
var _emailQuery = '';
var _emailOpen = null;   // the full message being read (shapeEmail)
var _emailImgUrls = [];  // v127.39 — object URLs for inline-rendered images; revoked on leave
var _emailUnread = 0;    // v127.45 — unread inbound emails; folded into the Comms nav badge
var _emailLoading = false;
var _emailErr = null;    // 'offline' (fetch failed) | 'notlive' (503/500 pre-go-live) | 'denied' (403, v136.9) | null
var _emailDeniedMsg = ''; // v136.9: the Worker's reason for a 403, shown as it came

// ── State (module-scoped; the PIN is deliberately NOT persisted anywhere) ─────
var _who = null;                 // { id, name } — localStorage 'chat_who_v1'
var _pin = null;                 // in-memory only
var _pinAt = 0;                  // last use — expires after 5 idle minutes
var PIN_IDLE_MS = 5 * 60 * 1000;
var _channels = [];
var _activeKey = null;           // open thread channel key
var _activeTitle = '';
var _pendingDm = null;           // 'store:X' | 'acct:N' — first send opens the DM
var _pollTimer = null;
var _sending = false;
var _pendingPhoto = null;        // compressed data-URL awaiting send (A6)
var _pendingVoice = null;        // v129.119 — { dataUrl, duration } 🎤 awaiting send
var _rec = null;                 // live MediaRecorder state while recording
var _voiceCache = {};            // message id -> blob URL, once played
var _pendingDoc = null;          // v125.93: { filename, content_type, data } awaiting send (Head Office: PDFs in chat)
var _photoCache = {};            // message id → data URL (avoid refetch on poll re-render)
var _reactCache = {};            // v127.49 — message id → reactions [{emoji,count,mine,names}] for the who-liked sheet
var _jackOpen = false;           // v125.20: the "Ask Jack" assistant thread is open
var _pickOpen = false;           // v125.39: the new-conversation picker is open (poll must not repaint over it)
var _chatFilter = '';            // v125.47: the conversation-list search text (28 stores + HO at launch)
var _jackMsgs = [];              // ephemeral Q&A this session — [{role:'user'|'assistant', text}] — never stored
var _jackBusy = false;

function loadWho() {
  if (_who) return _who;
  try {
    var raw = localStorage.getItem('chat_who_v1');
    if (raw) { var w = JSON.parse(raw); if (w && w.id && w.name) _who = { id: w.id, name: w.name }; }
  } catch (e) { /* fresh pick */ }
  return _who;
}
function saveWho(w) {
  _who = w ? { id: w.id, name: w.name } : null;  // id + name ONLY — never a PIN
  try {
    if (_who) localStorage.setItem('chat_who_v1', JSON.stringify(_who));
    else localStorage.removeItem('chat_who_v1');
  } catch (e) { /* storage unavailable — session-only */ }
}
function pinFresh() { return _pin && (Date.now() - _pinAt) < PIN_IDLE_MS; }
function clearPin() { _pin = null; _pinAt = 0; }

function root() { return document.getElementById('chatRoot'); }
function onChatScreen() {
  return (typeof currentScreen === 'undefined' || currentScreen === 'chat') && !document.hidden;
}

// ── The Jack/WhatsApp skin (injected once) ────────────────────────────────────
var WALLPAPER = 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23000\'%3E%3Ccircle cx=\'10\' cy=\'10\' r=\'1.5\'/%3E%3Ccircle cx=\'30\' cy=\'10\' r=\'1.5\'/%3E%3Ccircle cx=\'10\' cy=\'30\' r=\'1.5\'/%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'1.5\'/%3E%3Ccircle cx=\'20\' cy=\'20\' r=\'1.5\'/%3E%3C/g%3E%3C/svg%3E")';

function ensureStyles() {
  if (document.getElementById('waChatStyles')) return;
  var css = '' +
    '.wa-shell{display:flex;flex-direction:column;height:calc(100dvh - 160px);min-height:420px;max-width:560px;margin:0 auto;background:var(--chat-wall,#eae6df);border-radius:12px;overflow:hidden;position:relative;box-shadow:0 1px 6px rgba(0,0,0,.12)}' +
    // v125.41 (Matt): the WhatsApp-desktop two-pane layout — conversations on
    // the LEFT, the open thread on the RIGHT. Applied at >=900px only; phones
    // and tills keep the single-pane flow untouched.
    '.wa-2col{flex-direction:row;max-width:1100px}' +
    // v135.1: logical sides (border-inline-end, margin-inline-start, text-align:end) render the same in English and
    // mirror for Arabic right-to-left.
    '.wa-col-left{width:38%;min-width:300px;max-width:420px;border-inline-end:1px solid var(--border-strong);display:flex;flex-direction:column;background:var(--card);position:relative;overflow:hidden}' +
    '.wa-col-right{flex:1;display:flex;flex-direction:column;position:relative;overflow:hidden;background:var(--chat-wall,#eae6df)}' +
    '.wa-2col .wa-back{display:none}' +
    // v125.94 — pointer-events:none is LOAD-BEARING. The wallpaper is an
    // absolutely-positioned layer over the WHOLE pane; positioned elements
    // hit-test ABOVE static content, so without it every un-lifted row (the
    // Chat/Email toggle, the search box, the banners) was CLICK-DEAD — the
    // "can't click on Email" that survived three promotions (Matt, 2026-07-16).
    // Chat itself only ever worked because it's the default tab.
    '.wa-wp{position:absolute;inset:0;z-index:0;opacity:.04;pointer-events:none;background-image:' + WALLPAPER + '}' +
    '.wa-head{display:flex;align-items:center;gap:10px;padding:10px 14px;background:#075e54;z-index:10;flex-shrink:0;box-shadow:0 1px 4px rgba(0,0,0,.35);color:#fff}' +
    '.wa-back{background:none;border:none;color:#fff;font-size:20px;cursor:pointer;padding:2px 6px;line-height:1}' +
    'html[dir="rtl"] .wa-back{transform:scaleX(-1)}' +   // v136.8: in Arabic "back" points right, the way the page reads

    // v136.1 dark mode: the header avatar is a white tile on the teal bar in both themes, like a logo tile.
    '.wa-av{width:40px;height:40px;border-radius:50%;background:#fff;color:#075e54;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:15px;flex-shrink:0;border:2px solid rgba(255,255,255,.4)}' +
    '.wa-hname{font-weight:600;font-size:15px;line-height:1.2}' +
    '.wa-hsub{font-size:11px;color:#b2dfdb;margin-top:1px}' +
    '.wa-who{margin-inline-start:auto;font-size:11px;background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.35);color:#fff;border-radius:12px;padding:3px 10px;cursor:pointer;white-space:nowrap}' +
    '.wa-msgs{flex:1;overflow-y:auto;padding:12px 10px;z-index:1;display:flex;flex-direction:column;gap:4px}' +
    '.wa-msgs::-webkit-scrollbar{width:3px}.wa-msgs::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:3px}' +
    '.wa-datechip{text-align:center;margin:4px 0 10px}.wa-datechip span{font-size:11px;background:var(--chat-chip);color:var(--comms-sub);padding:4px 14px;border-radius:8px;font-weight:500}' +
    '.wa-row{display:flex;justify-content:flex-start;margin-bottom:2px}.wa-row.me{justify-content:flex-end}' +
    '.wa-bubble{max-width:85%;min-width:80px;border-radius:8px;padding:7px 10px 5px;box-shadow:0 1px 2px rgba(0,0,0,.12);background:var(--chat-in);border-top-left-radius:0}' +
    '.wa-row.me .wa-bubble{background:var(--chat-me,#d9fdd3);border-top-left-radius:8px;border-top-right-radius:0}' +
    '.wa-bname{font-size:12px;font-weight:700;color:var(--chat-accent);margin-bottom:3px}' +
    '.wa-btext{font-size:14px;line-height:1.5;color:var(--text);word-break:break-word;white-space:pre-wrap}' +
    '.wa-btime{font-size:10px;color:var(--sub);text-align:end;margin-top:2px}' +
    '.wa-del{font-size:13px;color:var(--comms-mute);font-style:italic}' +
    '.wa-input{display:flex;align-items:flex-end;gap:6px;padding:8px 10px 10px;background:var(--chat-bar);z-index:10;flex-shrink:0;border-top:1px solid var(--border-strong)}' +
    '.wa-text{flex:1;border:none;border-radius:20px;padding:11px 14px;font-size:14px;font-family:inherit;resize:none;max-height:90px;background:var(--chat-field);outline:none}' +
    // v125.55 — emoji picker (Matt: "can there [be] new emojis in Chat")
    '.wa-emoji{width:38px;height:38px;border:none;background:none;font-size:22px;cursor:pointer;flex-shrink:0;padding:0}' +
    '.wa-emoji-pop{position:absolute;bottom:64px;left:8px;right:8px;background:var(--card);border:1px solid var(--border-strong);border-radius:12px;padding:8px;display:flex;flex-wrap:wrap;gap:2px;max-height:190px;overflow-y:auto;z-index:40;box-shadow:0 4px 18px rgba(0,0,0,.2)}' +
    '.wa-emoji-pop button{border:none;background:none;font-size:22px;cursor:pointer;width:38px;height:38px;border-radius:8px;padding:0}' +
    '.wa-emoji-pop button:hover{background:var(--surface-3)}' +
    '.wa-send{width:44px;height:44px;border-radius:50%;border:none;background:#25d366;color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 2px 5px rgba(0,0,0,.2);font-size:18px}' +
    '.wa-send:disabled{opacity:.5}' +
    '.wa-list{flex:1;overflow-y:auto;z-index:1;background:var(--card)}' +
    '.wa-item{display:flex;align-items:center;gap:12px;padding:11px 14px;border-bottom:1px solid var(--surface-3);cursor:pointer;background:var(--card)}' +
    '.wa-item:hover{background:var(--surface-2)}' +
    '.wa-iav{width:44px;height:44px;border-radius:50%;background:var(--comms-face);color:var(--comms-sub);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:15px;flex-shrink:0}' +
    '.wa-imain{flex:1;min-width:0}' +
    '.wa-ititle{font-size:15px;font-weight:600;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
    '.wa-ipreview{font-size:12px;color:var(--sub);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:1px}' +
    '.wa-imeta{display:flex;flex-direction:column;align-items:flex-end;gap:4px;flex-shrink:0}' +
    '.wa-itime{font-size:11px;color:var(--sub)}' +
    '.wa-iunread{min-width:20px;height:20px;border-radius:10px;background:#25d366;color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0 6px}' +
    '.wa-fab{position:absolute;right:14px;bottom:16px;width:52px;height:52px;border-radius:50%;border:none;background:#25d366;color:#fff;font-size:22px;cursor:pointer;box-shadow:0 3px 8px rgba(0,0,0,.3);z-index:5}' +
    '.wa-notice{font-size:11px;color:var(--comms-sub);background:var(--chat-chip);text-align:center;padding:5px 10px;z-index:1}' +
    '.wa-pick{flex:1;overflow-y:auto;z-index:1;background:var(--card);padding:8px 0}' +
    '.wa-sect{font-size:11px;font-weight:700;color:var(--comms-sub);text-transform:uppercase;letter-spacing:.5px;padding:10px 16px 4px}' +
    // v126.55 — padding + box-sizing so a full-height card (the email compose)
    // can be sized against THIS overlay and still keep a visible margin. The
    // overlay is inset:0 of the comms PANE, not the viewport — that mismatch is
    // what put the Send button off-screen (see renderCompose).
    '.wa-modal{position:absolute;inset:0;background:var(--overlay);z-index:20;display:flex;align-items:center;justify-content:center;padding:12px;box-sizing:border-box}' +
    '.wa-card{background:var(--card);border-radius:14px;padding:18px;width:82%;max-width:320px;text-align:center}' +
    '.wa-pinin{width:100%;box-sizing:border-box;font-size:24px;letter-spacing:8px;text-align:center;padding:10px;border:1.5px solid var(--border-strong);border-radius:10px;margin:12px 0}' +
    '.wa-primary{width:100%;padding:12px;background:#075e54;color:#fff;border:none;border-radius:10px;font-size:15px;font-weight:600;cursor:pointer}' +
    '.wa-cancel{width:100%;padding:10px;background:none;color:var(--comms-sub);border:none;font-size:13px;cursor:pointer;margin-top:6px}' +
    '.wa-err{color:var(--comms-err);font-size:12px;min-height:16px;margin-top:4px}' +
    '.wa-attach{width:44px;height:44px;border-radius:50%;border:none;background:var(--chat-field);color:var(--chat-accent);cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 1px 3px rgba(0,0,0,.15);font-size:17px}' +
    '.wa-pendbar{display:flex;align-items:center;gap:8px;padding:6px 12px;background:var(--chat-bar);border-top:1px solid var(--border-strong);z-index:10}' +
    '.wa-pendbar img{max-height:64px;border-radius:8px;border:2px solid #25d366}' +
    '.wa-pendx{border:none;background:none;color:var(--comms-sub);font-size:16px;cursor:pointer}' +
    '.wa-photo{margin:2px 0 4px}.wa-photo img{max-width:100%;max-height:220px;border-radius:8px;cursor:pointer;display:block}' +
    '.wa-photo-ph{width:160px;height:100px;border-radius:8px;background:var(--surface-3);color:var(--comms-sub);font-size:12px;display:flex;align-items:center;justify-content:center}' +
    '.wa-viewer{position:fixed;inset:0;background:rgba(0,0,0,.88);z-index:9999;display:flex;align-items:center;justify-content:center;cursor:zoom-out}' +
    '.wa-viewer img{max-width:96vw;max-height:92vh;border-radius:6px}' +
    '.wa-bubble{position:relative}' +
    '.wa-reacts{display:flex;gap:4px;margin-top:3px;flex-wrap:wrap}' +                              // v125.86 👍
    '.wa-react{font-size:11.5px;background:var(--card);border:1px solid var(--border);border-radius:10px;padding:1px 7px;cursor:pointer;box-shadow:0 1px 1px rgba(0,0,0,.08);user-select:none}' +
    '.wa-react.mine{background:var(--chat-react-mine);border-color:var(--brand,#009E3D);font-weight:700}' +
    '.wa-mact{position:absolute;top:1px;right:3px;display:flex;gap:1px}' +
    // v125.72 — the quoted original inside a reply bubble
    '.wa-quote{border-inline-start:3px solid #25d366;background:var(--chat-quote);border-radius:6px;padding:4px 8px;margin:2px 0 5px;font-size:12px;color:var(--comms-sub);overflow:hidden}' +
    '.wa-quote b{display:block;color:var(--chat-accent);font-size:11.5px}' +
    '.wa-quote span{display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
    '.wa-mact button{border:none;background:none;font-size:10px;opacity:.35;cursor:pointer;padding:1px 3px}' +
    '.wa-mact button:hover{opacity:1}' +
    // v127.49 (the ops manager, #4): on PHONE widths an open thread takes the WHOLE
    // viewport (over the app header/nav — WhatsApp behaviour); ← goes back to
    // the windowed list where the app nav is reachable again. Applied via the
    // wa-full class threadShell adds on its narrow wrapper only, so the list
    // view and wide two-pane layout are untouched.
    // v129.25: widened 700 -> 899 and added the safe-area pad. Between 701px
    // and 899px NEITHER treatment applied — wa-full is set unconditionally by
    // threadShell but only styled at <=700, while the two-pane isWide() layout
    // needs >=900 — so phone-landscape and small tablets fell back to a
    // normal-flow shell and scrolled the header (and the back arrow) away,
    // the same failure Matt reported on the admin side. 899 cannot reach the
    // two-pane layout. The safe-area pad keeps the send button clear of an
    // iPhone home indicator now the shell is a fixed 100dvh.
    // v129.125 — the staff app carried the SAME pair of faults as the Head
    // Office bundle (found there first, from Matt's iPhone photo: the status bar
    // draws over the fixed header, so the 📹 and 🔔 sit underneath iOS). The
    // bottom safe area was padded when this shell went fixed; the top was
    // missed in the same edit. And top:0 + bottom:0 + height is over-constrained
    // — CSS drops `bottom`, so a 100dvh taller than the visible area overhangs
    // the screen until a keyboard-driven resize snaps it back. inset:0 alone is
    // exactly the viewport. Full reasoning in src/chatAdmin/index.js; this is
    // the same rule, and it must not diverge from it.
    '@media (max-width:899px){.wa-full{position:fixed;inset:0;min-height:0;max-width:none;margin:0;border-radius:0;z-index:6000}' +
    '.wa-full .wa-head{padding-top:calc(10px + env(safe-area-inset-top,0px))}' +
    '.wa-full .wa-input{padding-bottom:calc(10px + env(safe-area-inset-bottom,0px))}}';
  var el = document.createElement('style');
  el.id = 'waChatStyles';
  el.textContent = css;
  document.head.appendChild(el);
}

// ── Small render helpers ──────────────────────────────────────────────────────
function initials(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).map(function (w) { return w[0]; }).slice(0, 2).join('').toUpperCase() || '?';
}
function fmtTime(iso) {
  var d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}
function dayKey(iso) { var d = new Date(iso); return isNaN(d.getTime()) ? '' : d.toDateString(); }
function dayLabel(iso) {
  var d = new Date(iso); var now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  var y = new Date(now.getTime() - 86400000);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
function stripStorePrefix(t) { return String(t || '').replace(/^[^-]+-\s*/, ''); }

// v125.17 (Matt: the chat LIST should read like WhatsApp). WhatsApp shows the
// OTHER person's name for a 1:1, not "you ↔ them". Our DM titles are the
// canonical "A ↔ B"; from the viewer's side, drop your own half and show only
// who you're talking to (prefixes trimmed for a clean, scannable name).
function myChatLabel() {
  if (isAccountMode()) return (typeof currentUser !== 'undefined' && currentUser && currentUser.name) || _acctName || '';
  // v126.85 (a staff member, Dubai Hills: "other person's name is missing" on DMs) —
  // _who is {id, name} ONLY (deliberately never more, v125-era privacy), so
  // `_who.store` was ALWAYS undefined: no DM title half ever matched "mine",
  // and the fallback strip collapsed "Alex Example ↔ Dubai - Dubai Hills" to the
  // store's OWN name. A staff viewer's chat side is the STORE — read it from
  // the store CONTEXT global, like every other store-scoped call here.
  return (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : '';
}
function chatListTitle(c) {
  if (c && c.kind === 'dm') {
    var parts = String(c.title || '').split(' ↔ ');
    if (parts.length === 2) {
      var mine = myChatLabel();
      var other = (parts[0] === mine) ? parts[1] : (parts[1] === mine) ? parts[0] : null;
      if (other) return stripStorePrefix(other);
    }
    return stripStorePrefix(c.title);
  }
  if (c && c.kind === 'store') return stripStorePrefix(c.title);
  return (c && c.title) || '';
}
// Deterministic WhatsApp-style avatar colour from a name.
var CHAT_AV_COLORS = ['#e17076', '#7bc862', '#65aadd', '#a695e7', '#ee7aae', '#6ec9cb', '#f2749a', '#faa774', '#b09ff0', '#5ec9a5'];
function chatAvColor(s) {
  var h = 0; s = String(s || '');
  for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return CHAT_AV_COLORS[Math.abs(h) % CHAT_AV_COLORS.length];
}

// ── Two-pane primitives (v125.41) ─────────────────────────────────────────────
// WhatsApp-desktop layout: list LEFT, thread RIGHT, >=900px only. The scaffold
// lives inside root(); each renderer targets its pane when wide and falls back
// to the classic full-root single-pane flow when narrow. A resize across the
// breakpoint re-renders (the narrow renderers overwrite the scaffold, so the
// layout self-heals in both directions).
function isWide() { return typeof window !== 'undefined' && window.innerWidth >= 900; }
function paneLeft() { return document.getElementById('waPaneLeft'); }
function paneRight() { return document.getElementById('waPaneRight'); }
function ensureScaffold() {
  if (!isWide()) return false;
  var el = root(); if (!el) return false;
  if (!paneLeft()) {
    el.innerHTML = '<div class="wa-shell wa-2col">' +
      '<div class="wa-col-left" id="waPaneLeft"></div>' +
      '<div class="wa-col-right" id="waPaneRight"></div></div>';
  }
  return true;
}
// Paint the RIGHT pane from state: open thread / Jack / an open email / the
// "select a conversation" placeholder. Only navigation calls this — the 15s
// poll never does, so an open composer is never repainted over.
function renderRightPane() {
  var right = paneRight(); if (!right) return;
  // v126.52 — on the EMAIL tab the right pane belongs to email, full stop. This
  // ordering is defence in depth behind the commsTab() state clear: if any other
  // path ever leaves a thread key set while the email tab is showing, an opened
  // email must still win rather than silently rendering the old chat.
  if (_commsTab === 'email') {
    if (_emailOpen) { right.innerHTML = '<div class="wa-wp"></div>' + emailReadHtml(_emailOpen); return; }
  } else {
    if (_jackOpen) { renderJack(); return; }
    if (_activeKey || _pendingDm) { renderThread(); return; }
  }
  right.innerHTML = '<div class="wa-wp"></div>' +
    '<div style="flex:1;display:flex;align-items:center;justify-content:center;z-index:1;text-align:center;padding:24px;color:var(--comms-sub)">' +
    '<div><div style="font-size:40px;margin-bottom:10px">💬</div><div style="font-size:14px;font-weight:700;color:var(--text)">Select a conversation</div>' +
    '<div style="font-size:12.5px;margin-top:4px">Pick a chat or email on the left to read it here.</div></div></div>';
}
var _resizeBound = false;
var _wasWide = null;
function bindResize() {
  if (_resizeBound || typeof window === 'undefined') return;
  _resizeBound = true;
  _wasWide = isWide();
  window.addEventListener('resize', function () {
    var w = isWide();
    if (w !== _wasWide) { _wasWide = w; if (onChatScreen()) renderChatScreen(); }
  });
}

// ── Screen entry point (called by switchScreen('chat')) ──────────────────────
// v125.51 (Matt: "pin should only come after… also comms"): the chat list
// renders IMMEDIATELY, keyed by the signed-in store when nobody has picked a
// name yet. The who-picker appears only on demand — sending, Ask Jack — and
// the PIN only after that.
function renderChatScreen() {
  ensureStyles();
  bindResize();
  bindPaste();        // v125.100 — Ctrl+V a screenshot into the composer
  loadWho();          // restore a previous pick if there is one
  if (isWide()) { ensureScaffold(); renderList(); renderRightPane(); startPolling(); return; }
  if (_jackOpen) { renderJack(); return; }   // v125.20: the Ask-Jack thread owns the screen while open
  if (_activeKey || _pendingDm) renderThread(); else renderList();
  startPolling();
}

// "Who are you?" — the till user picks themselves (id + name only; PIN comes
// later, only when they SEND).
function renderWhoPicker() {
  var el = root(); if (!el) return;
  var staff = [];
  // v127.42 (a store manager's report #2, task-close picker 29 Jul): the identity picker shows
  // home staff + TODAY'S ROSTERED COVER (getStoreRoster's v113.0 second arg),
  // so a supporter transferred to another store can act under their own name
  // at the store they're covering — the same rule as clock/checks/tally.
  // Falls back to home-only where the roster helper isn't loaded (chat.html).
  try {
    if (typeof currentStore !== 'undefined') {
      var _rosterDay = (typeof businessDayStr === 'function') ? businessDayStr() : null;
      if (typeof getStoreRoster === 'function') staff = getStoreRoster(currentStore, _rosterDay) || [];
      else if (typeof getStoreStaff !== 'undefined') staff = getStoreStaff(currentStore) || [];
    }
  } catch (e) { staff = []; }
  var rows = staff.map(function (s) {
    return '<div class="wa-item" role="button" tabindex="0" onclick="Chat.pickWho(' + s.id + ',\'' + esc(String(s.name || '').replace(/'/g, '')) + '\')">' +
      (typeof Avatars !== 'undefined' ? Avatars.chip('staff', s.id, s.name, 40) : '<div class="wa-iav" translate="no">' + esc(initials(s.name)) + '</div>') +
      '<div class="wa-imain"><div class="wa-ititle" translate="no">' + esc(s.name) + '</div>' +
      '<div class="wa-ipreview">' + esc(s.role || '') + '</div></div></div>';
  }).join('');
  el.innerHTML = '<div class="wa-shell"><div class="wa-wp"></div>' +
    '<div class="wa-head"><div class="wa-av">💬</div><div><div class="wa-hname">Messages</div><div class="wa-hsub">Who are you?</div></div></div>' +
    '<div class="wa-notice">Pick your name so your messages are sent as you. You\'ll only need your PIN when you send.</div>' +
    '<div class="wa-notice" style="cursor:pointer;background:var(--surface-2);font-weight:700" onclick="if(typeof Avatars!==\'undefined\')Avatars.openCapture()">📷 Add / change your profile photo — it shows wherever your name appears</div>' +
    '<div class="wa-pick">' + (rows || '<div style="padding:20px;color:var(--comms-sub);font-size:13px;text-align:center">No staff found for this store yet.</div>') + '</div>' +
    '<div style="padding:12px;text-align:center;font-size:13px;font-weight:700;color:var(--comms-sub);cursor:pointer" onclick="Chat.cancelWhoPick()">‹ Back to chats</div></div>';
}
// One action waiting on a who-pick (e.g. tapped Send before picking a name).
var _pendingAction = null;
function pickWho(id, name) {
  saveWho({ id: id, name: name });
  clearPin();
  renderChatScreen();
  refreshChannels();
  // Resume the action that opened the picker (v125.51: identity on demand).
  if (_pendingAction) { var a = _pendingAction; _pendingAction = null; a(); }
}
// v125.51: the list no longer gates on a pick, so switching must open the
// picker explicitly. The open thread is KEPT — the Bob→Steve handover mid-
// conversation returns to the same thread, now sending as Steve.
function switchWho() { saveWho(null); clearPin(); renderWhoPicker(); }
function cancelWhoPick() { _pendingAction = null; renderChatScreen(); }

// ── Channel list (the WhatsApp "Chats" screen) ───────────────────────────────
// v125.19 (Matt): the Comms hub — one tab holding 💬 Chat + 📧 Email. It LANDS
// on Chat (the high-frequency surface, no extra tap); Email is a toggle away and
// shows "coming soon" until IT plumb store mail → tasks. The toggle sits under
// the header; switching it just re-renders, so chat state is never lost.
function commsToggleHtml() {
  function seg(id, label) {
    var on = _commsTab === id;
    return '<button onclick="Chat.commsTab(\'' + id + '\')" style="flex:1;border:none;cursor:pointer;font-family:inherit;font-size:13px;font-weight:700;padding:9px 0;background:' +
      (on ? 'var(--card)' : 'transparent') + ';color:' + (on ? 'var(--chat-accent)' : 'var(--comms-sub)') + ';border-bottom:2px solid ' + (on ? 'var(--chat-accent)' : 'transparent') + '">' + label + '</button>';
  }
  // v128.53 (Stage B): with Chat + Email as their own nav tabs, the in-screen
  // sub-tab chrome is dropped — the nav IS the switch. chat.html (no bottom
  // nav) keeps the segmented control.
  if (document.getElementById('nav-email')) return '';
  return '<div style="display:flex;background:var(--chat-bar);flex-shrink:0">' + seg('chat', '💬 Chat') + seg('email', '📧 Email') + '</div>';
}
// v125.90 — STORE EMAIL IS LIVE (2026-07-16: Matt's end-to-end test landed in
// Arabian Centre's inbox via ALGT's Bluehost forwarders). This state now only
// renders when the inbox API errors (503/500 → 'notlive'), so the copy is an
// honest "can't reach it" — never "coming soon" again. Function name kept:
// the notlive→fallback routing (and its test pins) are unchanged.
function emailComingSoonHtml() {
  return '<div style="flex:1;overflow-y:auto;background:var(--card);display:flex;align-items:center;justify-content:center;text-align:center;padding:32px 24px">' +
    '<div><div style="font-size:40px;margin-bottom:12px">📧</div>' +
    '<div style="font-size:16px;font-weight:800;color:var(--text);margin-bottom:6px">Store Email</div>' +
    '<div style="font-size:13px;color:var(--comms-sub);line-height:1.6;max-width:300px">Your store\'s inbox can\'t be reached right now. Check the connection and try again in a moment.</div>' +
    '<button onclick="Chat.refreshEmails()" style="margin-top:14px;border:1px solid var(--border-strong);background:var(--card);color:var(--chat-accent);font-weight:700;font-size:13px;padding:8px 18px;border-radius:18px;cursor:pointer;font-family:inherit">↻ Try again</button></div></div>';
}
// v136.9 (Matt, 17 Oct 2026): store mail is Head Office's (every store), an area manager's (their
// area) and the store's own. Anyone else gets the Worker's 403 reason in words, not "can't reach it".
function emailDeniedHtml() {
  return '<div style="flex:1;overflow-y:auto;background:var(--card);display:flex;align-items:center;justify-content:center;text-align:center;padding:32px 24px">' +
    '<div><div style="font-size:40px;margin-bottom:12px">📧</div>' +
    '<div style="font-size:16px;font-weight:800;color:var(--text);margin-bottom:6px">Store Email</div>' +
    '<div style="font-size:13px;color:var(--comms-sub);line-height:1.6;max-width:300px">' + esc(_emailDeniedMsg || 'Store email is not available for this account.') + '</div></div></div>';
}

function fmtEmailWhen(iso) {
  if (!iso) return '';
  try {
    var d = new Date(iso);
    var today = new Date();
    if (d.toDateString() === today.toDateString()) return fmtTime(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch (e) { return ''; }
}

function commsEmailHtml() {
  // v125.41: on wide screens the open email renders in the RIGHT pane
  // (renderRightPane) — the left keeps showing the inbox list.
  if (_emailOpen && !isWide()) return emailReadHtml(_emailOpen);
  if (_emailErr === 'denied') return emailDeniedHtml();   // v136.9
  if (_emailErr === 'notlive' || (_emailErr && _emails === null)) return emailComingSoonHtml();
  if (_emails === null) {
    return '<div style="flex:1;overflow-y:auto;background:var(--card);display:flex;align-items:center;justify-content:center;color:var(--comms-sub);font-size:13px;padding:32px">Loading your store inbox…</div>';
  }
  // v126.79 (Doha - Landmark Mall's request): Inbox and Sent are separate
  // views now — a folder strip filters the one loaded list by direction, so
  // no new API calls. Sent rows title by recipient, not sender.
  var folderBtn = function (f, label) {
    var on = _emailFolder === f;
    return '<button onclick="Chat.emailFolder(\'' + f + '\')" style="flex:1;border:none;padding:9px 0;font-family:inherit;font-size:13px;font-weight:800;cursor:pointer;background:var(--card);border-bottom:2.5px solid ' + (on ? 'var(--chat-accent)' : 'transparent') + ';color:' + (on ? 'var(--chat-accent)' : 'var(--comms-sub)') + '">' + label + '</button>';
  };
  var strip = '<div style="display:flex;background:var(--card);border-bottom:1px solid var(--border-strong)">' + folderBtn('in', '📥 Inbox') + folderBtn('out', '↗️ Sent') + '</div>';
  // The sort row. An ACTIVE key shows its direction and flips on a second tap;
  // an inactive one just switches. Three keys, because those are the three the
  // shop floor asked for and a fourth would only be a fourth thing to scan.
  var sortBtn = function (k, label) {
    var on = _emailSort.key === k;
    var arrow = on ? (_emailSort.dir === 'asc' ? ' ↑' : ' ↓') : '';
    return '<button onclick="Chat.emailSort(\'' + k + '\')" style="border:1px solid ' + (on ? '#075e54' : 'var(--border-strong)') +
      ';background:' + (on ? '#075e54' : 'var(--card)') + ';color:' + (on ? '#fff' : 'var(--comms-sub)') +
      ';font-family:inherit;font-size:11.5px;font-weight:700;padding:4px 10px;border-radius:14px;cursor:pointer;white-space:nowrap">' +
      label + arrow + '</button>';
  };
  var sortStrip = '<div style="display:flex;gap:6px;align-items:center;background:var(--card);padding:7px 10px;border-bottom:1px solid var(--border);overflow-x:auto">' +
    '<span style="font-size:11px;color:var(--comms-mute);font-weight:700;flex-shrink:0">Sort</span>' +
    sortBtn('date', 'Date') + sortBtn('who', whoLabel(_emailFolder)) + sortBtn('subject', 'Subject') +
    '</div>';
  // v129.106 (Dubai Hills: "Search bar in inbox and sent items … just like in
  // chat"). Same shape as the conversation search above the chat list — one
  // box, live as you type, searching what the ROW SHOWS (subject, from/to,
  // preview). It filters the loaded list, so no API call and no new endpoint.
  var searchStrip = '<div style="padding:8px 10px;background:var(--card);border-bottom:1px solid var(--surface-3);display:flex;gap:6px;align-items:center">' +
    '<input id="waEmailSearch" type="search" placeholder="🔍 Search ' + (_emailFolder === 'out' ? 'sent items' : 'inbox') + '" value="' + esc(_emailQuery) + '" ' +
      'oninput="Chat.emailSearch(this.value)" style="flex:1;min-width:0;box-sizing:border-box;border:none;background:var(--surface-3);border-radius:18px;padding:8px 14px;font-size:13px;font-family:inherit;outline:none">' +
    (_emailQuery
      ? '<button onclick="Chat.emailSearch(\'\')" title="Clear the search" style="border:none;background:transparent;color:var(--chat-accent);font-weight:700;font-size:12px;cursor:pointer;font-family:inherit;flex-shrink:0">Clear</button>'
      : '') +
    '</div>';
  var header = '<div style="background:var(--comms-note-bg);color:var(--comms-note-ink);font-size:12px;padding:8px 12px;text-align:center;display:flex;align-items:center;justify-content:space-between;gap:8px">' +
      '<span>📧 Your store\'s email inbox · 🔒 Visible to Head Office</span>' +
      '<button onclick="Chat.refreshEmails()" style="border:none;background:transparent;color:var(--chat-accent);font-weight:700;font-size:12px;cursor:pointer;font-family:inherit;flex-shrink:0">↻ Refresh</button></div>';
  var fab = '<button class="wa-fab" onclick="Chat.composeEmail()" title="New email">✉️</button>';
  var folderList = _emails.filter(function (m) { return _emailFolder === 'out' ? m.direction === 'out' : m.direction !== 'out'; });
  // v129.106 — search narrows BEFORE the sort (cheaper, and identical either
  // way). filterEmails never mutates and returns the list untouched when the box
  // is empty, so the no-search path is exactly what it was before v129.106.
  var list = filterEmails(folderList, _emailQuery, _emailFolder);
  var searching = list.length !== folderList.length || (_emailQuery && _emailQuery.trim());
  // v129.44 (Dubai Hills: "quick way of sorting mails … by sender, date,
  // subject"). sortEmails NEVER mutates — _emails is the cache the unread
  // count and the reader both read, and reordering it in place would quietly
  // reorder them too.
  list = sortEmails(list, { key: _emailSort.key, dir: _emailSort.dir, folder: _emailFolder });
  // Sorting only sorts what is LOADED, and searching only searches what is
  // loaded — the second is the more dangerous of the two, because zero matches
  // reads as "that email does not exist" when the truth is "it is in a page
  // nobody has fetched". A live query therefore OWNS this line (stacking two
  // amber bars would just get both ignored).
  var note = searching
    ? searchNote(_emailQuery, _emailsHasMore, list.length)
    : sortNote(_emailSort.key, _emailsHasMore);
  var noteHtml = note
    ? '<div style="background:var(--comms-note-bg);color:var(--comms-note-ink);font-size:11.5px;padding:6px 12px;line-height:1.45">' + esc(note) + '</div>'
    : '';
  // The count is the receipt for the filter: it says what the box did, so a
  // short list never looks like a short mailbox.
  var countHtml = searching
    ? '<div style="background:var(--card);color:var(--comms-sub);font-size:11.5px;padding:6px 12px;border-bottom:1px solid var(--surface-3)">' +
      (list.length
        ? '<b>' + list.length + '</b> of ' + folderList.length + ' ' + (_emailFolder === 'out' ? 'sent emails' : 'emails') + ' match "' + esc(String(_emailQuery).trim()) + '"'
        : 'No ' + (_emailFolder === 'out' ? 'sent emails' : 'emails') + ' match "' + esc(String(_emailQuery).trim()) + '"') +
      '</div>'
    : '';
  strip = strip + sortStrip + searchStrip + countHtml + noteHtml;
  if (!list.length) {
    // A search that matched nothing is NOT an empty mailbox — saying "No emails
    // yet" to somebody who just typed a name would be a plain lie. Separate
    // state, with the way out (clear the search / load older) in it.
    var emptyBody = searching
      ? '<div><div style="font-size:40px;margin-bottom:10px">🔍</div>' +
        '<div style="font-size:15px;font-weight:800;color:var(--text);margin-bottom:6px">Nothing matched</div>' +
        '<div style="font-size:13px;color:var(--comms-sub);line-height:1.6;max-width:320px">No ' + (_emailFolder === 'out' ? 'sent email' : 'email') + ' here matches "' + esc(String(_emailQuery).trim()) + '".' +
        (_emailsHasMore ? ' Older emails have not been loaded yet — load them and search again.' : '') + '</div>' +
        '<div style="margin-top:14px;display:flex;gap:8px;justify-content:center;flex-wrap:wrap">' +
          '<button onclick="Chat.emailSearch(\'\')" style="border:1px solid var(--border-strong);background:var(--card);color:var(--chat-accent);font-weight:700;font-size:13px;padding:8px 18px;border-radius:18px;cursor:pointer;font-family:inherit">Clear search</button>' +
          (_emailsHasMore ? '<button onclick="Chat.loadOlderEmails()" style="border:1px solid var(--border-strong);background:var(--card);color:var(--chat-accent);font-weight:700;font-size:13px;padding:8px 18px;border-radius:18px;cursor:pointer;font-family:inherit">⬇ Load older emails</button>' : '') +
        '</div></div>'
      : _emailFolder === 'out'
        ? '<div><div style="margin-bottom:12px">' + (typeof Artwork !== 'undefined' && Artwork.balloonSvg ? Artwork.balloonSvg(88) : '<span style="font-size:40px">📮</span>') + '</div>' +
          '<div style="font-size:15px;font-weight:800;color:var(--text);margin-bottom:6px">No sent emails yet</div>' +
          '<div style="font-size:13px;color:var(--comms-sub);line-height:1.6;max-width:300px">Emails your store sends will appear here. Tap ✉️ to write one.</div></div>'
        : '<div><div style="margin-bottom:12px">' + (typeof Artwork !== 'undefined' && Artwork.balloonSvg ? Artwork.balloonSvg(88) : '<span style="font-size:40px">📭</span>') + '</div>' +
          '<div style="font-size:15px;font-weight:800;color:var(--text);margin-bottom:6px">No emails yet</div>' +
          '<div style="font-size:13px;color:var(--comms-sub);line-height:1.6;max-width:300px">Emails sent to your store\'s address will appear here.</div>' +
          '<button onclick="Chat.refreshEmails()" style="margin-top:14px;border:1px solid var(--border-strong);background:var(--card);color:var(--chat-accent);font-weight:700;font-size:13px;padding:8px 18px;border-radius:18px;cursor:pointer;font-family:inherit">↻ Refresh</button></div>';
    return header + strip +
      '<div style="flex:1;overflow-y:auto;background:var(--card);display:flex;align-items:center;justify-content:center;text-align:center;padding:32px 24px">' + emptyBody + '</div>' + fab;
  }
  var rows = list.map(function (m) {
    var unread = m.direction === 'in' && !m.read_at;
    // v129.4 (Meadows Town Center's idea): SUBJECT is the title — who it's
    // from/to moves to the second line. Much faster to scan an inbox by topic.
    // v135.1: the addresses and the preview are what people wrote, so they are marked translate="no"; the labels
    // around them ("To:", "(unknown sender)") still translate.
    var toList = (m.to || []).join(', ');
    var who = m.direction === 'out'
      ? 'To: ' + (toList ? '<span translate="no">' + esc(toList) + '</span>' : '(unknown)')
      : (m.from ? '<span translate="no">' + esc(m.from) + '</span>' : '(unknown sender)');
    // v131.18 (Mercato): a Sent row whose recipient(s) bounced says so in red
    // — "Sent" with a silently-dead recipient is how four permit emails went
    // nowhere while the store believed they had gone.
    var fails = (m.direction === 'out' && Array.isArray(m.delivery_failures)) ? m.delivery_failures : [];
    var failLine = fails.length
      ? '<div style="color:var(--comms-err);font-size:11.5px;font-weight:700;margin-top:2px">⚠️ Not delivered to <span translate="no">' +
        esc(fails.map(function (f) { return f.address; }).join(', ')) + '</span></div>'
      : '';
    return '<div class="wa-item" role="button" tabindex="0" onclick="Chat.openEmail(' + Number(m.id) + ')">' +
      '<div class="wa-iav" style="background:' + (fails.length ? 'var(--danger-bg)' : 'var(--ui-green-soft)') + '">' + (m.direction === 'out' ? (fails.length ? '⚠️' : '↗️') : '📧') + '</div>' +
      '<div class="wa-imain"><div class="wa-ititle"' + (unread ? ' style="font-weight:800"' : '') + (m.subject ? ' translate="no">' + esc(m.subject) : '>(no subject)') + '</div>' +
      '<div class="wa-ipreview"' + (unread ? ' style="color:var(--text);font-weight:600"' : '') + '>' + who + (m.preview ? ' — <span translate="no">' + esc(m.preview) + '</span>' : '') + '</div>' + failLine + '</div>' +
      '<div class="wa-imeta"><div class="wa-itime">' + esc(fmtEmailWhen(m.received_at)) + '</div>' +
      (m.has_attachments ? '<div style="font-size:12px">📎</div>' : '') +
      (unread ? '<div class="wa-iunread">●</div>' : '') + '</div></div>';
  }).join('');
  // v127.35 (Doha - Landmark Mall): older mail exists past the 50-row page —
  // give it a visible door instead of a silent cliff.
  var older = _emailsHasMore
    ? '<div style="text-align:center;padding:12px">' +
      '<button onclick="Chat.loadOlderEmails()" style="border:1px solid var(--border-strong);background:var(--card);color:var(--chat-accent);font-weight:700;font-size:13px;padding:8px 18px;border-radius:18px;cursor:pointer;font-family:inherit">⬇ Load older emails</button></div>'
    : '<div style="text-align:center;padding:10px;color:var(--comms-mute);font-size:11.5px">No older emails</div>';
  return header + strip + '<div class="wa-list">' + rows + older + '</div>' + fab;
}

// The read screen — text only. The API never returns html (v125.36 pin) and
// every field is esc()'d before it touches innerHTML.
function emailReadHtml(m) {
  // v125.95 (Matt: "works — but i cant click the attachments?") — chips with a
  // Resend attachment id are BUTTONS now: the Worker proxies the file from
  // Resend on demand and the v125.93 iOS-safe opener shows it. Chips without
  // an id (older rows) keep the inert look + the fallback note.
  var anyOpenable = false;
  var atts = (m.attachments || []).map(function (a) {
    if (a && a.id != null) {
      anyOpenable = true;
      // v127.29 (27 Jul audit P1): STRIP quotes, don't HTML-encode them — the
      // browser decodes the onclick attribute before the JS engine parses it,
      // so an encoded quote in the source becomes a live quote inside the JS
      // string (attacker = any external sender crafting a MIME filename).
      // Same strip-then-esc pattern as pickWho/startDm.
      var fnJs = esc(String(a.filename || 'attachment').replace(/'/g, ''));
      return '<button onclick="Chat.openEmailAttachment(' + Number(m.id) + ',\'' + esc(String(a.id).replace(/'/g, '')) + '\',\'' + fnJs + '\')" style="font-size:12px;color:var(--chat-accent);font-weight:700;padding:6px 10px;background:var(--surface-3);border:1px solid var(--border-strong);border-radius:8px;display:inline-block;margin:3px 4px 0 0;cursor:pointer;font-family:inherit">📎 <span translate="no">' + esc(a.filename || 'attachment') + '</span></button>';
    }
    return '<div style="font-size:12px;color:var(--comms-sub);padding:6px 10px;background:var(--surface-3);border-radius:8px;display:inline-block;margin:3px 4px 0 0">📎 <span translate="no">' + esc(a.filename || 'attachment') + '</span></div>';
  }).join('');
  // v129.104: SENT mail before the MAIL bucket existed has filenames but no
  // bytes (Resend never gives a sent attachment back) — say so plainly rather
  // than leaving a chip that looks broken. Anything sent from now on is kept
  // and opens normally.
  var attNote = anyOpenable
    ? 'Tap an attachment to open it.'
    : (m.direction === 'out'
      ? 'This was sent before attachments were kept — the file is in the store\'s normal email.'
      : 'Attachments open in the store\'s normal email for now.');
  // v126.62 (the ops manager, at Arabian Centre: "couldn't find the reply button until u
  // scroll down to the bottom of the Email body... expected Reply/FWD on top
  // like Outlook"). The Reply/Forward buttons used to sit AFTER the body, so on
  // a long email you scrolled the whole thing to find them. They now live in the
  // sticky header — always on screen, back-to-inbox on the left, actions on the
  // right, exactly the Outlook layout. Small, mirror-identical to the full-size
  // buttons that remain at the foot for anyone who replies after reading.
  var topActions =
    (m.direction === 'in'
      ? '<button onclick="Chat.composeEmail(' + Number(m.id) + ')" style="border:1px solid var(--chat-accent);background:var(--card);color:var(--chat-accent);font-weight:700;font-size:12.5px;padding:5px 12px;border-radius:16px;cursor:pointer;font-family:inherit">↩ Reply</button>' +
        // v129.144 — only when there IS someone beyond the sender; otherwise
        // Reply all would do exactly what Reply does.
        (hasMoreThanSender(m, selfEmailAddrs())
          ? '<button onclick="Chat.composeEmail(' + Number(m.id) + ',true)" title="Reply to everyone on the original email" style="border:1px solid var(--chat-accent);background:var(--card);color:var(--chat-accent);font-weight:700;font-size:12.5px;padding:5px 12px;border-radius:16px;cursor:pointer;font-family:inherit">↩↩ Reply all</button>'
          : '')
      : '') +
    '<button onclick="Chat.composeEmailForward(' + Number(m.id) + ')" style="border:1px solid var(--chat-accent);background:var(--card);color:var(--chat-accent);font-weight:700;font-size:12.5px;padding:5px 12px;border-radius:16px;cursor:pointer;font-family:inherit">↪ Forward</button>';
  return '<div style="flex:1;overflow-y:auto;background:var(--card)">' +
    '<div style="padding:12px 14px;border-bottom:1px solid var(--border);position:sticky;top:0;background:var(--card);z-index:1">' +
      '<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px">' +
        '<button onclick="Chat.emailBack()" style="border:none;background:transparent;color:var(--chat-accent);font-weight:700;font-size:13px;cursor:pointer;font-family:inherit;padding:0">‹ Inbox</button>' +
        '<div style="display:flex;gap:6px;flex-shrink:0">' + topActions + '</div>' +
      '</div>' +
      '<div style="font-size:15px;font-weight:800;color:var(--text);line-height:1.4"' + (m.subject ? ' translate="no">' + esc(m.subject) : '>(no subject)') + '</div>' +
      '<div style="font-size:12px;color:var(--comms-sub);margin-top:4px">' + (m.direction === 'out' ? 'To: <span translate="no">' + esc((m.to || []).join(', ')) + '</span>' : 'From: ' + (m.from ? '<span translate="no">' + esc(m.from) + '</span>' : '(unknown)')) + '</div>' +
      '<div style="font-size:11px;color:var(--comms-mute);margin-top:2px">' + esc(fmtEmailWhen(m.received_at)) + (m.sent_by ? ' · sent by <span translate="no">' + esc(m.sent_by) + '</span>' : '') + '</div>' +
      // v131.18: the read screen names each failed recipient with the bounce
      // reason, in plain words - the fix is almost always a re-typed address.
      ((Array.isArray(m.delivery_failures) && m.delivery_failures.length)
        ? '<div style="margin-top:8px;background:var(--comms-red-soft);border:1px solid var(--comms-red-line);border-radius:8px;padding:8px 10px;font-size:12px;color:var(--danger-deep);line-height:1.5">' +
          '<b>⚠️ This email did NOT reach:</b><br>' +
          m.delivery_failures.map(function (f) {
            return '<span translate="no">' + esc(f.address) + (f.reason ? ' — ' + esc(f.reason) : '') + '</span>' + (f.reason ? '' : (f.status === 'complained' ? ' — marked as spam' : ' — bounced'));
          }).join('<br>') +
          '<div style="margin-top:4px;color:var(--danger-deep)">Check the address spelling, then Forward this email to the corrected address.</div></div>'
        : '') +
      (atts ? '<div style="margin-top:6px">' + atts + '<div style="font-size:11px;color:var(--comms-mute);margin-top:4px">' + attNote + '</div></div>' : '') +
    '</div>' +
    // v135.1: the email body is what someone wrote, so it is never translated (the pending and empty notes are ours
    // and do translate).
    // v136.1 dark mode: the read view is the TEXT body only (the sender's HTML never reaches the app), so it carries
    // no colours of its own and follows the theme like any other text.
    '<div class="em-body" style="padding:14px;font-size:14px;color:var(--text);line-height:1.6;white-space:pre-wrap;word-break:break-word"' +
      (m.body_state !== 'pending' && m.text_body ? ' translate="no"' : '') + '>' +
      (m.body_state === 'pending' ? '<em style="color:var(--comms-sub)">The message body is still on its way — pull refresh in a moment.</em>' : ((typeof RichText !== 'undefined' && RichText.formatRich)
        ? RichText.formatRich(m.text_body || '(no text in this email)', { breaks: false })
        : emailLinkify(esc(m.text_body || '(no text in this email)')))) +
    '</div>' +
    // v127.39 (Doha - Landmark Mall, 27 Jul: an image pasted into the body
    // "shows white blanks only"): the read view is TEXT-only by design
    // (the rich body never leaves the server — v125.36 pin), so an inline
    // image becomes an invisible gap. Render IMAGE attachments as thumbnails under
    // the body instead — fetched via the SAME gated attachment proxy and set
    // as object-URLs on DOM-created <img>s (no markup from the payload ever
    // touches innerHTML). Chips above stay for save/open-full.
    emailInlineImgPlaceholders(m) +
    '<div style="padding:0 14px 20px;display:flex;gap:8px">' +
      (m.direction === 'in'
        ? '<button onclick="Chat.composeEmail(' + Number(m.id) + ')" style="border:1px solid var(--chat-accent);background:var(--card);color:var(--chat-accent);font-weight:700;font-size:13px;padding:9px 22px;border-radius:18px;cursor:pointer;font-family:inherit">↩ Reply</button>' +
          (hasMoreThanSender(m, selfEmailAddrs())
            ? '<button onclick="Chat.composeEmail(' + Number(m.id) + ',true)" title="Reply to everyone on the original email" style="border:1px solid var(--chat-accent);background:var(--card);color:var(--chat-accent);font-weight:700;font-size:13px;padding:9px 22px;border-radius:18px;cursor:pointer;font-family:inherit">↩↩ Reply all</button>'
            : '')
        : '') +
      // v126.14 (Matt: "e-mail doesnt have a forward button") — both directions.
      '<button onclick="Chat.composeEmailForward(' + Number(m.id) + ')" style="border:1px solid var(--chat-accent);background:var(--card);color:var(--chat-accent);font-weight:700;font-size:13px;padding:9px 22px;border-radius:18px;cursor:pointer;font-family:inherit">↪ Forward</button>' +
    '</div>' +
    '</div>';
}

// Fetch the inbox for the current viewer. Staff -> staff_id (the Worker
// derives the store); account -> Bearer + the current store's name.
async function loadEmails() {
  if (_emailLoading) return;
  _emailLoading = true;
  try {
    var q;
    if (isAccountMode()) {
      var st = (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : null;
      q = '/api/email/inbox' + (st ? '?store=' + encodeURIComponent(st) : '');
    } else {
      // v125.91 (Matt: "remove the block on that for stores") — a till with no
      // picked name reads the store's inbox via the validated ?store= identity
      // (readIdQ, the v125.51 pattern). The old `if (!_who) return` bailed
      // SILENTLY and left "Loading your store inbox…" up forever — the "email
      // is blocked for stores" experience.
      var rid = readIdQ();
      if (!rid) { _emailErr = 'offline'; _emailLoading = false; if (_commsTab === 'email' && !_activeKey) renderList(); return; }
      q = '/api/email/inbox?' + rid + coverStoreQ();   // v132.74: the device's store, honoured when it is in the person's cover set
    }
    var res = await api(q);
    if (!res.ok) { _emailErr = (res.status === 503 || res.status === 500) ? 'notlive' : 'offline'; }
    // v136.9: a 403 is not "can't reach it": this account (or this store) is not one whose mail it may read. Say so.
    if (res.status === 403) { _emailErr = 'denied'; _emailDeniedMsg = ''; try { var dj = await res.json(); _emailDeniedMsg = (dj && dj.error) || ''; } catch (e3) { /* the default line */ } }
    else if (res.ok) {
      var data = await res.json();
      _emails = (data && Array.isArray(data.emails)) ? data.emails : [];
      // v127.35 (Doha - Landmark Mall, 27 Jul): the Worker pages at 50 and the
      // client only ever loaded page one — a busy store's older mail (their
      // "before 22 July") silently fell off with no load-more and no message.
      // A FULL page means older mail exists; the Load-older row picks it up.
      _emailsHasMore = _emails.length >= 50;
      _emailErr = null;
      recountEmailUnread();   // v127.45 — keep the Comms badge honest
    }
  } catch (e) { _emailErr = 'offline'; }
  _emailLoading = false;
  if (_commsTab === 'email' && !_activeKey) renderList();
}

// v127.35 — fetch the next (older) page via the Worker's existing `before`
// param, keyed off the oldest received_at we hold. Appends + dedupes by id, so
// a re-tap or an overlapping timestamp can never double a row.
async function loadOlderEmails() {
  if (_emailLoading || !Array.isArray(_emails) || !_emails.length) return;
  _emailLoading = true;
  try {
    var oldest = _emails.reduce(function (min, m) {
      return (m.received_at && (!min || m.received_at < min)) ? m.received_at : min;
    }, null);
    if (!oldest) { _emailsHasMore = false; _emailLoading = false; renderList(); return; }
    var q;
    if (isAccountMode()) {
      var st = (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : null;
      q = '/api/email/inbox' + (st ? '?store=' + encodeURIComponent(st) : '');
    } else {
      var rid = readIdQ();
      if (!rid) { _emailLoading = false; return; }
      q = '/api/email/inbox?' + rid + coverStoreQ();   // v132.74: the device's store, honoured when it is in the person's cover set
    }
    q += (q.indexOf('?') !== -1 ? '&' : '?') + 'before=' + encodeURIComponent(oldest);
    var res = await api(q);
    if (res.ok) {
      var data = await res.json();
      var page = (data && Array.isArray(data.emails)) ? data.emails : [];
      var seen = {};
      _emails.forEach(function (m) { seen[m.id] = true; });
      page.forEach(function (m) { if (!seen[m.id]) _emails.push(m); });
      _emailsHasMore = page.length >= 50;
    }
  } catch (e) { /* keep what we have; the row stays for a retry */ }
  _emailLoading = false;
  if (_commsTab === 'email' && !_activeKey) renderList();
}

async function openEmail(id) {
  try {
    var q = '/api/email/message?id=' + encodeURIComponent(id) +
      (isAccountMode() ? '' : (readIdQ() ? '&' + readIdQ() : ''));   // v125.91: staff_id OR ?store=
    var res = await api(q);
    // v126.52 — SAY something. This returned silently on every failure, so a
    // store could only ever report "it is not opening" and we had nothing to go
    // on; the attachment opener two functions down has always toasted. Whatever
    // the cause, the person tapping deserves to know it failed.
    if (!res.ok) {
      var e1 = await res.json().catch(function () { return {}; });
      if (typeof showToast !== 'undefined') showToast((e1 && e1.error) || ('Could not open the email (' + res.status + ')'));
      return;
    }
    var data = await res.json();
    if (data && data.email) {
      _emailOpen = data.email;
      // reflect the server-side read stamp locally so the dot clears at once
      (_emails || []).forEach(function (m) { if (m.id === data.email.id && !m.read_at) m.read_at = data.email.read_at || new Date().toISOString(); });
      recountEmailUnread();   // v127.45 — reading clears it from the badge
      renderList();
      if (isWide()) renderRightPane(); // wide: the read view opens on the right
      revokeInlineEmailImages();
      loadInlineEmailImages(data.email);   // v127.39 — inline image thumbnails, best-effort
    }
  } catch (e) {
    if (typeof showToast !== 'undefined') showToast('Could not open the email — check the connection');
  }
}

// v127.45 — recount unread inbound emails from whatever list we hold and
// repaint the nav badge. Called after every inbox load/read; the 60s
// always-on loop also fetches a quiet page when the email tab has never been
// opened, so the badge is honest for someone living on the Clock tab.
function recountEmailUnread(list) {
  _emailUnread = (list || _emails || []).filter(function (m) { return m && m.direction !== 'out' && !m.read_at; }).length;
  try { updateNavBadge(); } catch (e) {}
}
async function refreshEmailUnread() {
  if (Array.isArray(_emails)) { recountEmailUnread(); return; }
  try {
    var q;
    if (isAccountMode()) {
      var st = (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : null;
      if (!st) return;
      q = '/api/email/inbox?store=' + encodeURIComponent(st);
    } else {
      var rid = readIdQ();
      if (!rid) return;
      q = '/api/email/inbox?' + rid + coverStoreQ();   // v132.74: the device's store, honoured when it is in the person's cover set
    }
    var res = await api(q);
    if (!res.ok) return;
    var data = await res.json();
    recountEmailUnread((data && data.emails) || []);
  } catch (e) { /* badge refresh is best-effort */ }
}

// v127.41 — make urls in the TEXT body tappable. Input is the ALREADY-
// ESCAPED body string (esc() ran first), so nothing here can introduce
// markup: we only wrap a matched, still-escaped url span in a fixed-attribute
// anchor. Trailing punctuation and escaped quote/angle entities are trimmed
// off the match so "…action. (https://x)." links cleanly.
function emailLinkify(escaped) {
  return String(escaped).replace(/https?:\/\/[^\s]+/g, function (u) {
    var cut = u.length;
    ['&quot;', '&lt;', '&gt;', '&#39;'].forEach(function (ent) {
      var i = u.indexOf(ent); if (i !== -1 && i < cut) cut = i;
    });
    var core = u.slice(0, cut).replace(/[),.;:!?]+$/, '');
    var rest = u.slice(core.length);
    if (!core) return u;
    return '<a href="' + core + '" target="_blank" rel="noopener noreferrer" style="color:var(--chat-accent);font-weight:700;word-break:break-all">' + core + '</a>' + rest;
  });
}

// v127.39 — which attachments qualify for inline thumbnails: images with a
// fetchable id, capped at 6 per email (a mail merge of 40 screenshots must
// not fire 40 fetches on open).
function emailInlineImgAtts(m) {
  return ((m && m.attachments) || [])
    .filter(function (a) { return a && a.id != null && /^image\//.test(String(a.content_type || '')); })
    .slice(0, 6);
}
function emailInlineImgPlaceholders(m) {
  var atts = emailInlineImgAtts(m);
  if (!atts.length) return '';
  return '<div style="padding:0 14px 6px">' + atts.map(function (a, i) {
    return '<div id="emIimg' + i + '" style="margin:0 0 10px;color:var(--comms-mute);font-size:12px">📷 Loading ' + esc(a.filename || 'image') + '…</div>';
  }).join('') + '</div>';
}
// Fetch each image through the gated proxy and swap the placeholder for a
// DOM-built <img> (src = object URL — nothing from the payload reaches
// innerHTML). Failure degrades to a pointer at the tappable chip above.
async function loadInlineEmailImages(m) {
  var atts = emailInlineImgAtts(m);
  for (var i = 0; i < atts.length; i++) {
    var slot = document.getElementById('emIimg' + i);
    if (!slot) continue;   // view changed while loading
    try {
      var q = '/api/email/attachment?id=' + encodeURIComponent(m.id) + '&att=' + encodeURIComponent(atts[i].id) + '&stream=1' +
        (isAccountMode() ? '' : '&' + readIdQ());
      var res = await api(q);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      var blob = await res.blob();
      var slot2 = document.getElementById('emIimg' + i);
      if (!slot2) continue;
      var urlObj = URL.createObjectURL(blob);
      _emailImgUrls.push(urlObj);
      var img = document.createElement('img');
      img.src = urlObj;
      img.alt = atts[i].filename || 'image';
      img.style.cssText = 'max-width:100%;border-radius:10px;display:block';
      slot2.textContent = '';
      slot2.appendChild(img);
    } catch (e) {
      var slot3 = document.getElementById('emIimg' + i);
      if (slot3) slot3.textContent = '📷 ' + (atts[i].filename || 'image') + ' — tap the attachment above to view it.';
    }
  }
}
function revokeInlineEmailImages() {
  _emailImgUrls.forEach(function (u) { try { URL.revokeObjectURL(u); } catch (e) {} });
  _emailImgUrls = [];
}

// v125.95 — fetch one inbound attachment through the Worker (Resend pass-
// through, same read gate as the email itself) and open it with the v125.93
// iOS-safe opener. The Worker's error strings are user-ready (too big / provider
// unreachable), so they surface verbatim.
// v126.87 (issue #814, Mercato Mall: a >8 MB "elc artwork template" refused to
// open) — ask for ?stream=1 so the Worker PIPES the file straight to us (no
// 8 MB cap, nothing buffered as base64, the API key stays server-side).
// Success is a raw binary body → read it as a Blob; only a FAILURE is JSON,
// and its user-ready error surfaces verbatim.
async function openEmailAttachment(emailId, attId, filename) {
  if (typeof showToast !== 'undefined') showToast('Fetching attachment…');
  try {
    var q = '/api/email/attachment?id=' + encodeURIComponent(emailId) + '&att=' + encodeURIComponent(attId) + '&stream=1' +
      (isAccountMode() ? '' : '&' + readIdQ());
    var res = await api(q);
    if (!res.ok) {
      var d = await res.json().catch(function () { return {}; });
      if (typeof showToast !== 'undefined') showToast((d && d.error) || ('Could not open the attachment (' + res.status + ')'));
      return;
    }
    var blob = await res.blob();
    var ct = (res.headers && res.headers.get && res.headers.get('content-type')) || (blob && blob.type) || 'application/octet-stream';
    openChatBlob(blob, filename || 'attachment', ct);
  } catch (e) { if (typeof showToast !== 'undefined') showToast('Could not open the attachment — check the connection'); }
}

// v126.87 — the iOS-safe opener for a streamed Blob (the >8 MB attachment
// path). Same posture as the data-URL viewer (images → in-app viewer; everything
// else → window.open with a download fallback when a popup is blocked) but
// from a Blob straight off the stream, so nothing ever becomes a giant
// base64 string.
function openChatBlob(blob, filename, contentType) {
  var mime = contentType || (blob && blob.type) || 'application/octet-stream';
  var u = URL.createObjectURL(blob);
  if (String(mime).indexOf('image/') === 0) {
    var v = document.createElement('div');
    v.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.9);z-index:99999;display:flex;align-items:center;justify-content:center;cursor:zoom-out';
    v.innerHTML = '<img src="' + u + '" style="max-width:96vw;max-height:92vh;border-radius:6px">';
    v.addEventListener('click', function () { v.remove(); setTimeout(function () { try { URL.revokeObjectURL(u); } catch (e2) {} }, 1000); });
    document.body.appendChild(v);
    return;
  }
  var w = window.open(u, '_blank');
  if (!w) {                                     // popup blocked → download fallback
    var a = document.createElement('a');
    a.href = u; a.download = filename || 'attachment'; a.target = '_blank';
    document.body.appendChild(a); a.click(); a.remove();
  }
  setTimeout(function () { try { URL.revokeObjectURL(u); } catch (e) {} }, 60000);
}

// ── v125.96 — Forward (the ops manager: "forward an Image from Comms to another
// user … i expect a drill down button to choose, like reply button"). ────────
// WhatsApp-style: ↪ on the bubble → pick a destination from your OWN chat
// list → the text/photo/document is re-sent there with an '↪ Forwarded'
// marker. All existing endpoints — the photo/attachment are fetched on demand
// and go through the normal send (the server enforces who may post where; a
// till forward is PIN-attributed exactly like a normal send).
var _forward = null;   // { id, body, hasPhoto, hasAtt } while the sheet is open

function startForward(id, hasPhoto, hasAtt, btn) {
  var bubble = btn && btn.closest ? btn.closest('.wa-bubble') : null;
  var body = (bubble && bubble.getAttribute('data-body')) || '';
  _forward = { id: id, body: body, hasPhoto: !!hasPhoto, hasAtt: !!hasAtt };
  var sheet = document.createElement('div');
  sheet.id = 'waFwdSheet';
  sheet.style.cssText = 'position:fixed;inset:0;background:var(--overlay);z-index:99998;display:flex;align-items:flex-end;justify-content:center';
  var items = (_channels || []).map(function (c) {
    var k = jsArg(c.key);
    return '<div onclick="Chat.forwardTo(\'' + k + '\')" style="display:flex;align-items:center;gap:10px;padding:11px 14px;border-bottom:1px solid var(--surface-3);cursor:pointer;background:var(--card)">' +
      '<span style="font-size:18px">' + (c.kind === 'company' ? '📢' : c.kind === 'ho' ? '🏢' : c.kind === 'area' ? '🗺️' : c.kind === 'country' ? '🌍' : c.kind === 'store' ? '🏪' : '👤') + '</span>' +
      '<span style="font-size:13.5px;color:var(--text);font-weight:600"' + (c.kind === 'dm' ? ' translate="no"' : '') + '>' + esc(chatListTitle(c)) + '</span></div>';
  }).join('');
  sheet.innerHTML = '<div style="background:var(--card);width:100%;max-width:440px;max-height:70vh;border-radius:14px 14px 0 0;overflow:hidden;display:flex;flex-direction:column">' +
    '<div style="display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border-bottom:1px solid var(--border)">' +
      '<div style="font-weight:800;font-size:14px;color:var(--text)">↪ Forward to…</div>' +
      '<button onclick="Chat.closeForward()" style="border:none;background:transparent;font-size:16px;cursor:pointer;color:var(--comms-sub)">✕</button></div>' +
    '<div style="overflow-y:auto">' + (items || '<div style="padding:20px;color:var(--comms-sub);font-size:13px;text-align:center">No conversations yet</div>') + '</div></div>';
  sheet.addEventListener('click', function (e) { if (e.target === sheet) closeForward(); });
  document.body.appendChild(sheet);
}
function closeForward() {
  _forward = null;
  var s = document.getElementById('waFwdSheet');
  if (s) s.remove();
}
async function forwardTo(destKey) {
  var fwd = _forward;
  if (!fwd) { closeForward(); return; }
  // A till forward is a SEND — same PIN bar as typing a message.
  if (!isAccountMode() && !pinFresh()) { askPin(function () { forwardTo(destKey); }); return; }
  closeForward();
  if (typeof showToast !== 'undefined') showToast('↪ Forwarding…');
  try {
    var payload = isAccountMode() ? {} : { auth_staff_id: _who.id, auth_pin: _pin };
    payload.channel = destKey;
    payload.body = fwd.body ? '↪ Forwarded:\n' + fwd.body : '';
    if (fwd.hasPhoto) {
      var pr = await api('/api/chat/photo?id=' + encodeURIComponent(fwd.id) + (isAccountMode() ? '' : '&' + readIdQ()));
      var pd = pr.ok ? await pr.json() : null;
      if (pd && pd.photo) payload.photo = pd.photo;
    }
    if (fwd.hasAtt) {
      var ar = await api('/api/chat/attachment?id=' + encodeURIComponent(fwd.id) + (isAccountMode() ? '' : '&' + readIdQ()));
      var ad = ar.ok ? await ar.json() : null;
      if (ad && ad.data) payload.attachment = { filename: ad.filename, content_type: ad.content_type, data: ad.data };
    }
    if (!payload.body && !payload.photo && !payload.attachment) {
      if (typeof showToast !== 'undefined') showToast('Nothing to forward from that message');
      return;
    }
    var res = await api('/api/chat/send', { method: 'POST', body: JSON.stringify(payload) });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || d.ok === false) {
      if (typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not forward');
      return;
    }
    _pinAt = Date.now();
    if (typeof showToast !== 'undefined') showToast('↪ Forwarded');
    if (destKey === _activeKey) { await refreshThread(); }
  } catch (e) { if (typeof showToast !== 'undefined') showToast('Could not forward — check the connection'); }
}

function emailBack() { _emailOpen = null; revokeInlineEmailImages(); renderList(); if (isWide()) renderRightPane(); }
function refreshEmails() { _emails = null; _emailsHasMore = false; _emailErr = null; renderList(); loadEmails(); }
// v132.74 (Spinneys idea, 28 Sep 2026: "if you are covering a store you
// should see the emails that is intended to that store"). The Worker has
// honoured ?store= for a person's cover stores since v129.32, but the staff
// read only ever sent staff_id, so a cover person saw their HOME store's
// inbox while standing in the shop they were covering. Name the device's
// store; the Worker ignores it if they are not rostered there.
function coverStoreQ() {
  var st = (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : null;
  return st ? '&store=' + encodeURIComponent(st) : '';
}
// v126.79 — switch the email view between Inbox and Sent (filters the loaded
// list; no refetch). Closes any open email so the list is what you see.
function emailFolder(f) { _emailFolder = f === 'out' ? 'out' : 'in'; _emailOpen = null; renderList(); }
// v129.44 — tapping the active key flips direction; tapping another switches to
// it. A new key starts at the direction that is USEFUL for it: newest-first for
// dates, A-Z for names and subjects. Starting subject at Z-A because the last
// key happened to be descending would just look broken.
function emailSort(k) {
  if (_emailSort.key === k) _emailSort.dir = _emailSort.dir === 'asc' ? 'desc' : 'asc';
  else _emailSort = { key: k, dir: k === 'date' ? 'desc' : 'asc' };
  try { localStorage.setItem('elcEmailSort', JSON.stringify(_emailSort)); } catch (e) { /* private mode */ }
  renderList();
}
// v129.106 (Dubai Hills) — live mail search. Exactly the shape filterChats has
// used since v125.47 on these same tills: renderList repaints the pane, which
// would blur the input mid-typing, so focus + caret are restored after. One
// filtering path (the render pipeline) rather than a second DOM-toggling one
// that could drift from it. Not persisted — see _emailQuery.
// Deliberately does NOT close an open email: on a narrow screen the reader
// REPLACES the list (so the box is not reachable while reading), and on a wide
// one the list is the left pane — yanking the right-hand email away while
// somebody types would be the v126.52 hijack in reverse.
function emailSearch(v) {
  _emailQuery = String(v == null ? '' : v);
  renderList();
  var inp = document.getElementById('waEmailSearch');
  if (inp) { inp.focus(); try { inp.setSelectionRange(inp.value.length, inp.value.length); } catch (e) {} }
}

// ── Compose / reply (v125.38, Store Email PR3) ──────────────────────────────
// Anyone can OPEN compose; the Worker is the gate — staff sends are
// PIN-attributed (verifyPinViaRpc), and the From: address is always the store's
// mapped sending identity, never client-chosen. Any server refusal surfaces its
// message right in the modal.
// v126.59: the manager-only restriction is GONE (Matt's call). Al Seef Village
// could not reply to their own email for a week because the manager was on
// holiday — and the refusal was a dead end, since the PIN prompt asks for the
// CURRENTLY-PICKED person's PIN, so entering the manager's code under your own
// name failed too. PIN + attribution is the control now, not role.
var _compose = null; // { to, subject, replyToId }

// v129.144 — `all` = REPLY ALL (Mercato Mall: "there is no Reply All option").
// Reply is unchanged and still answers the sender alone; Reply all pre-fills
// every original recipient. Two buttons rather than one changed default: a
// reply that silently widens its audience is how a private answer ends up in
// front of a whole distribution list.
// v129.145 — the addresses that are US, so Reply all never mails the store
// itself. These come from the Worker (store_email_map, shipped on the read as
// self_addrs) because the client CANNOT derive them: a store has TWO addresses
// (the real @ALGT-me.com one and the @in.elctoysfeedback.com letterbox, per
// v153-0) and only the letterbox is strippable by suffix.
//
// v129.144 guessed at two client-side sources and BOTH were always undefined:
// `matched_address` is set at ingest but shapeEmail does not ship it, and
// `currentStore` is a store NAME string, not an object, so `.email` never
// existed. That left the self-strip dead: Reply all put the store's own
// @ALGT-me.com address in To:, the store mailed itself, and the reply
// forwarded straight back into its own inbox — the exact failure the original
// comment said it was preventing.
function selfEmailAddrs() {
  var out = [];
  try {
    if (_emailOpen && Array.isArray(_emailOpen.self_addrs)) {
      _emailOpen.self_addrs.forEach(function (a) { if (typeof a === 'string' && a.trim()) out.push(a); });
    }
  } catch (e) { /* best effort — the letterbox-suffix rule still applies */ }
  return out;
}

function composeEmail(replyToId, all) {
  if (replyToId && _emailOpen && _emailOpen.id === replyToId) {
    var subj = String(_emailOpen.subject || '').trim() || '(no subject)';
    // v136.29: the earlier email goes in below, like Outlook, so the chain is not lost (buildReplyQuote)
    var quote = buildReplyQuote(_emailOpen);
    _compose = {
      to: all ? replyAllRecipients(_emailOpen, selfEmailAddrs()).join(', ')
              : (_emailOpen.from_addr || ''),
      subject: /^re:/i.test(subj) ? subj : 'Re: ' + subj,
      replyToId: replyToId,
      draft: quote,
    };
    if (!quote && _emailOpen.body_state === 'pending' && typeof showToast !== 'undefined') {
      showToast('The earlier message could not be included below your reply because its text has not arrived. Your reply still goes in the same thread.');
    }
  } else {
    _compose = { to: '', subject: '', replyToId: null };
  }
  renderCompose();
}

function closeCompose() {
  _compose = null;
  var m = document.getElementById('waEmailModal');
  if (m) m.remove();
}
// v125.39 (Matt): Head-Office people as ready-made contacts. Accounts are set
// up in admin -> People -> Accounts with an email field — those become native
// suggestions on the To input (a <datalist>), and free typing still works for
// anyone outside the company. Cached per session; only rows whose email
// actually LOOKS like an email are offered (the column doubles as a login ID).
var _contacts = null;
async function loadContacts() {
  if (_contacts !== null) return _contacts;
  try {
    // v131.38 (security sweep C1): /api/accounts no longer carries emails on
    // the tokenless plane. The 📇 phone book (/api/contacts, store-viewer
    // gated) is where Head Office addresses live; same datalist, same shape.
    var storeQ = (typeof currentStore !== 'undefined' && currentStore) ? '?store=' + encodeURIComponent(currentStore) : '';
    var res = await api('/api/contacts' + storeQ);
    var d = await res.json();
    var people = [];
    ((d && Array.isArray(d.head_office)) ? d.head_office : []).forEach(function (g) {
      ((g && Array.isArray(g.people)) ? g.people : []).forEach(function (p) { people.push(p); });
    });
    _contacts = people.filter(function (p) {
      return p && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(p.email || ''));
    }).map(function (p) { return { name: p.name || '', email: String(p.email).toLowerCase() }; });
  } catch (e) { _contacts = []; }
  return _contacts;
}
function fillContactsDatalist() {
  loadContacts().then(function (list) {
    var dl = document.getElementById('emContacts');
    if (!dl) return;
    dl.innerHTML = list.map(function (c) {
      return '<option value="' + esc(c.email) + '">' + esc(c.name) + '</option>';
    }).join('');
  });
}

// v126.14 (Matt: "screen for replies doesnt feel very big with no options for
// bold, italic etc") — the compose card is near-fullscreen now, the body is a
// contenteditable with a B/I/U/list toolbar (html sent alongside the plain
// text; text stays what we store), plus 📎 attachments and a 📖 address book.
function textToHtml(t) {
  return esc(String(t || '')).replace(/\n/g, '<br>');
}

// v136.63 (war game, xss): the draft's HTML went back into the editor (and out in the email) as it was, so
// anything pasted in with it (an event handler, a script, a javascript: link) came along. Parsed in an inert
// <template> (nothing runs while it is read), the dangerous parts are removed; bold, italic, lists and links stay.
var EM_DROP_TAGS = /^(script|style|iframe|object|embed|link|meta|base|form|input|button|textarea|select|svg|math)$/i;
function cleanDraftHtml(html) {
  if (!html || typeof document === 'undefined') return html || '';
  var tpl = document.createElement('template');
  tpl.innerHTML = String(html);
  var all = tpl.content.querySelectorAll('*');
  for (var i = all.length - 1; i >= 0; i--) {
    var el = all[i];
    if (EM_DROP_TAGS.test(el.tagName)) { el.remove(); continue; }
    for (var j = el.attributes.length - 1; j >= 0; j--) {
      var a = el.attributes[j];
      var n = a.name.toLowerCase();
      if (n.indexOf('on') === 0 || n === 'srcdoc' || n === 'formaction' ||
          ((n === 'href' || n === 'src' || n === 'xlink:href' || n === 'action') && /^\s*(javascript|vbscript|data:text\/html)/i.test(a.value))) {
        el.removeAttribute(a.name);
      }
    }
  }
  return tpl.innerHTML;
}

function emAttChipsHtml() {
  var atts = (_compose && _compose.atts) || [];
  if (!atts.length) return '';
  return atts.map(function (a, i) {
    return '<span style="font-size:12px;color:var(--chat-accent);font-weight:700;padding:5px 8px;background:var(--surface-3);border:1px solid var(--border-strong);border-radius:8px;display:inline-flex;align-items:center;gap:6px;margin:0 6px 6px 0">📎 <span translate="no">' + esc(a.filename) + '</span>' +
      '<span onclick="Chat.emAttachRemove(' + i + ')" style="cursor:pointer;color:var(--comms-red);font-weight:900">✕</span></span>';
  }).join('');
}

// v129.69 — the PERSISTENT record of forwarded attachments that could not come
// along, one line per file with the reason. A toast disappears in 3 seconds;
// this stays in the compose until it is sent or closed, so "where did my
// attachment go?" (Mercato Mall) has a visible answer. Also shows the
// in-flight state while forward copies are still loading.
function emAttNoteHtml() {
  if (!_compose) return '';
  var out = '';
  if (_compose.attsLoading) {
    out += '<div style="font-size:12px;color:var(--comms-sub);margin:2px 0 6px">⏳ Bringing the original attachments over…</div>';
  }
  var notes = _compose.attNotes || [];
  if (notes.length) {
    out += '<div style="font-size:12px;color:var(--comms-amber-ink);background:var(--comms-note-bg);border:1px solid var(--comms-note-line);border-radius:8px;padding:7px 10px;margin:2px 0 6px;line-height:1.45">' +
      notes.map(function (n) { return '⚠️ <b translate="no">' + esc(n.filename) + '</b> ' + esc(n.why); }).join('<br>') +
      '</div>';
  }
  return out;
}

function renderCompose() {
  var el = root(); if (!el || !_compose) return;
  var old = document.getElementById('waEmailModal');
  if (old) old.remove();
  var title = _compose.replyToId ? 'Reply' : (_compose.isForward ? 'Forward' : 'New email');
  var btn = 'border:1px solid var(--border-strong);background:var(--card);border-radius:8px;min-width:36px;padding:7px 10px;font-size:13.5px;cursor:pointer;font-family:inherit;color:var(--text)';
  var wrap = document.createElement('div');
  wrap.id = 'waEmailModal';
  wrap.className = 'wa-modal';
  // v126.55 — size against the OVERLAY (100%), not the viewport (90vh). Dubai
  // Hills: "can not get to send button on e-mail". .wa-modal is inset:0 of the
  // comms PANE, but this card asked for 90vh of the WINDOW. On a short desktop
  // window the topbar + bottom nav take ~120px, so the pane was ~575px while the
  // card demanded ~657px — it overflowed its centred overlay at BOTH ends
  // (the To row was clipped too), and overflow:hidden made Send unreachable.
  // Phones never hit it: there the pane is nearly the whole screen, so 90vh fit.
  wrap.innerHTML = '<div class="wa-card" style="width:min(720px,96%);max-width:none;height:100%;max-height:100%;box-sizing:border-box;display:flex;flex-direction:column;text-align:start;overflow:hidden">' +
    '<div style="font-weight:700;font-size:15px;color:var(--text)">' + title + '</div>' +
    // v126.59 — "Managers only" is gone (Matt's call, after Al Seef Village
    // could not reply to their own email because the manager was on holiday).
    // The line still says whose name goes on it, because that IS the control now.
    // Precise, because the two halves differ: the RECIPIENT sees the store's
    // address ("Dubai - Wafi City <wafi@algt-me.com>"), while Shift records the
    // sender's name against it. Saying "in your name" implied the customer sees
    // you, which is not true — and that internal record is exactly the control
    // that replaced the manager gate, so it is worth stating plainly.
    '<div style="font-size:12px;color:var(--comms-sub);margin:4px 0 10px">Sent from your store\'s email address. Your name is saved against it, so Head Office can see who sent it.</div>' +
    '<div style="display:flex;gap:6px;margin-bottom:8px">' +
      '<input id="emTo" type="text" list="emContacts" placeholder="To (email address — comma for more than one)" style="flex:1;box-sizing:border-box;border:1px solid var(--border-strong);border-radius:8px;padding:10px;font-size:14px;font-family:inherit">' +
      '<button title="Address book" onclick="Chat.openAddressBook()" style="' + btn + '">📖</button>' +
    '</div>' +
    // v126.58 — a PERSISTENT hint, not just the placeholder. Arabian Ranches 2
    // reported "NO OPTION, ONLY ONE RECEPIENT IS ALLOWED"; multiple recipients
    // have always worked, but the only thing that ever said so was placeholder
    // text — which vanishes the moment you type, and gets truncated anyway on a
    // narrow window because the field shares its row with 📖. A feature nobody
    // can discover is, from the shop floor, a feature that does not exist.
    '<div style="font-size:11.5px;color:var(--comms-sub);margin:-4px 0 8px 2px">Sending to several people? Separate the addresses with a comma — or tap 📖 and add them one by one (up to 20).</div>' +
    '<datalist id="emContacts"></datalist>' +
    '<input id="emSubj" type="text" placeholder="Subject" maxlength="200" style="width:100%;box-sizing:border-box;border:1px solid var(--border-strong);border-radius:8px;padding:10px;font-size:14px;font-family:inherit;margin-bottom:8px">' +
    '<div style="display:flex;gap:6px;margin-bottom:6px;flex-wrap:wrap">' +
      '<button title="Bold" onclick="Chat.emCmd(\'bold\')" style="' + btn + ';font-weight:900">B</button>' +
      '<button title="Italic" onclick="Chat.emCmd(\'italic\')" style="' + btn + ';font-style:italic">I</button>' +
      '<button title="Underline" onclick="Chat.emCmd(\'underline\')" style="' + btn + ';text-decoration:underline">U</button>' +
      '<button title="Bullet list" onclick="Chat.emCmd(\'insertUnorderedList\')" style="' + btn + '">• List</button>' +
      '<button title="Attach a file" onclick="Chat.emAttachPick()" style="' + btn + '">📎 Attach</button>' +
      '<input id="emFile" type="file" multiple style="display:none" onchange="Chat.emAttachPicked(this)">' +
    '</div>' +
    // v126.82 (Mercato Mall: Send button gone with 6 attachments) — the chips
    // area is CAPPED and scrolls internally. A flex child's min-height:auto
    // means it never shrinks below its content, so six long-named chips pushed
    // the Send row out of the card bottom (the v126.55 fix protected the
    // EDITOR, not this row). Send stays reachable at the 10-attachment max.
    '<div id="emAtts" style="line-height:1;max-height:96px;overflow-y:auto;flex-shrink:0">' + emAttChipsHtml() + '</div>' +
    '<div id="emAttNote" style="flex-shrink:0">' + emAttNoteHtml() + '</div>' +
    // v126.55 — min-height:0, not 120px. A flex child will not shrink below its
    // min-height, so on a short window the editor held the card open and pushed
    // the Send row out of the bottom. It scrolls internally already
    // (overflow-y:auto), so shrinking costs nothing; the message is never cut off.
    // v136.1 dark mode: the email being WRITTEN stays white paper with dark text in both themes, on purpose. Pasted
    // text brings its own colours (often black), which a dark editor would hide, and this is what gets sent.
    '<div id="emBody" contenteditable="true" style="flex:1;min-height:0;overflow-y:auto;box-sizing:border-box;background:#fff;color:#111b21;border:1px solid var(--border-strong);border-radius:8px;padding:12px;font-size:14px;font-family:inherit;line-height:1.55;outline:none" data-placeholder="Write your message"></div>' +
    '<div class="wa-err" id="emErr"></div>' +
    // flex-shrink:0 — Send/Cancel are the point of the screen; they must be the
    // LAST thing to give up space, never the first.
    '<div style="display:flex;gap:8px;margin-top:10px;flex-shrink:0">' +
      '<button class="wa-primary" style="flex:1;margin:0" onclick="Chat.sendEmailNow()">Send</button>' +
      '<button class="wa-cancel" style="flex:1;margin:0" onclick="Chat.closeCompose()">Cancel</button>' +
    '</div></div>';
  var shell = el.querySelector('.wa-shell') || el;
  shell.appendChild(wrap);
  // values set via the DOM (never string-interpolated into the markup)
  wrap.querySelector('#emTo').value = _compose.to || '';
  wrap.querySelector('#emSubj').value = _compose.subject || '';
  // v126.82 (Doha - Landmark: pasted pictures "shown as blank") — an INLINE
  // pasted image can never survive the send (the html body is size-capped and
  // a pasted blob dies with the tab), so image pastes become ATTACHMENTS
  // instead: same journey as 📎, same caps, visible as a chip immediately.
  // Text pastes are untouched.
  var emBodyEl = wrap.querySelector('#emBody');
  if (emBodyEl) emBodyEl.addEventListener('paste', function (ev) {
    var items = (ev.clipboardData && ev.clipboardData.items) || [];
    var files = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].kind === 'file' && /^image\//.test(items[i].type || '')) {
        var f = items[i].getAsFile();
        if (f) files.push(f);
      }
    }
    if (!files.length) return;
    ev.preventDefault();
    emIngestFiles(files, 'pasted-image');
  });
  // v125.98: the who-picker rebuilds the screen — the draft survives (html
  // first, else the plain text turned into <br> lines, e.g. a forward quote).
  wrap.querySelector('#emBody').innerHTML = cleanDraftHtml(_compose.draftHtml) || textToHtml(_compose.draft || '');   // v136.63
  wrap.querySelector((_compose.to ? '#emBody' : '#emTo')).focus();
  fillContactsDatalist();
}

function emCmd(cmd) {
  var body = document.getElementById('emBody');
  if (body) body.focus();
  try { document.execCommand(cmd, false, null); } catch (e) { /* unsupported — typing still works */ }
}

// ── 📎 outbound attachments (mirror of the Worker caps: 3 files, 5MB each,
// 10MB together — checked here too so the error is instant, not a round-trip).
// v126.61 — MUST mirror MAX_SEND_ATTACHMENTS / SEND_ATT_MAX_BYTES /
// SEND_ATT_TOTAL_BYTES in src/worker/store-email.js. The client cap exists to
// give a fast, friendly refusal; the Worker is the actual gate. A test pins the
// two together so they can never drift — a client that allows more than the
// server would mean uploading 10 MB only to be rejected at the end.
var EM_ATT_MAX = 10, EM_ATT_FILE_BYTES = 5 * 1024 * 1024, EM_ATT_TOTAL_BYTES = 10 * 1024 * 1024;

function emAttachPick() {
  var f = document.getElementById('emFile');
  if (f) f.click();
}

// v126.82 — the ONE ingestion path for compose attachments, shared by the 📎
// picker and the editor's paste handler (same caps, same chips). fallbackBase
// names files that arrive nameless (a pasted screenshot is often just
// "image.png" — number them so chips stay tellable-apart).
function emIngestFiles(files, fallbackBase) {
  if (!_compose || !files || !files.length) return;
  _compose.atts = _compose.atts || [];
  var errEl = document.getElementById('emErr');
  Array.prototype.slice.call(files).forEach(function (file, idx) {
    if (_compose.atts.length >= EM_ATT_MAX) { if (errEl) errEl.textContent = 'Max ' + EM_ATT_MAX + ' attachments per email'; return; }
    if (file.size > EM_ATT_FILE_BYTES) { if (errEl) errEl.textContent = (file.name || 'That file') + ' is too big (max 5 MB per file)'; return; }
    var total = _compose.atts.reduce(function (s, a) { return s + (a.size || 0); }, 0);
    if (total + file.size > EM_ATT_TOTAL_BYTES) { if (errEl) errEl.textContent = 'Attachments are too big together (max 10 MB)'; return; }
    var name = file.name;
    if ((!name || name === 'image.png') && fallbackBase) {
      var ext = (file.type || '').split('/')[1] || 'png';
      name = fallbackBase + '-' + (_compose.atts.length + idx + 1) + '.' + ext;
    }
    var reader = new FileReader();
    reader.onload = function () {
      var b64 = String(reader.result || '').split(',')[1] || '';
      if (!b64) return;
      _compose.atts.push({ filename: name || 'attachment', content_type: file.type || 'application/octet-stream', content: b64, size: file.size });
      var box = document.getElementById('emAtts');
      if (box) box.innerHTML = emAttChipsHtml();
    };
    reader.readAsDataURL(file);
  });
}

function emAttachPicked(input) {
  if (!input || !input.files || !input.files.length) return;
  emIngestFiles(input.files, null);
  input.value = '';
}

function emAttachRemove(i) {
  if (!_compose || !_compose.atts) return;
  _compose.atts.splice(i, 1);
  var box = document.getElementById('emAtts');
  if (box) box.innerHTML = emAttChipsHtml();
}

// ── ↪ Forward (v126.14) — quote the original below a header; openable
// attachments come along too (fetched via the existing proxy, so they ride
// the same caps — a fetch failure just notes the file instead of blocking).
async function composeEmailForward(id) {
  if (!_emailOpen || _emailOpen.id !== id) return;
  var m = _emailOpen;
  // v129.139 (field report: forwards saying "please see below" arrive with
  // NOTHING below) — the quote is built from m.text_body, and a row whose
  // body fetch is still pending (or failed for good — Resend no longer holds
  // the message, so it stays pending forever) quoted an EMPTY string. The
  // read view says "the body is still on its way", but Forward stayed live,
  // so the sender typed their note above an empty quote and never noticed.
  // Refuse loudly instead of forwarding silence.
  if (m.body_state === 'pending') {
    if (typeof showToast !== 'undefined') showToast('The content of this email has not arrived yet, so there is nothing to forward — open it again in a moment and retry');
    return;
  }
  if (!String(m.text_body || '').trim()) {
    if (typeof showToast !== 'undefined') showToast('This email has no text to forward — the content may be attachment-only; send the attachments or forward from the normal store email');
    return;
  }
  var subj = String(m.subject || '').trim() || '(no subject)';
  var quoted = '\n\n---------- Forwarded message ----------\n' +
    'From: ' + (m.from || '(unknown)') + '\n' +
    'Date: ' + fmtEmailWhen(m.received_at) + '\n' +
    'Subject: ' + subj + '\n\n' +
    (m.text_body || '');
  // v129.69 (Mercato Mall: "the attachements is not showing and include while
  // forwarding") — the old flow FILTERED OUT id-less attachments before the
  // loop (no message at all) and reported every other failure as a transient
  // toast that vanished before anyone read it. Now EVERY original attachment
  // is accounted for: it either arrives as a chip, or its name appears in a
  // persistent note in the compose saying WHY it could not come along
  // (attNotes, rendered by emAttNoteHtml). Send is blocked while the copies
  // are still in flight (attsLoading) so a quick Send can't race the loop.
  _compose = { to: '', subject: /^fwd:/i.test(subj) ? subj : 'Fwd: ' + subj, replyToId: null, isForward: true, draft: quoted, atts: [], attNotes: [], attsLoading: true };
  renderCompose();
  var all = (m.attachments || []).filter(function (a) { return !!a; });
  // v129.80 (Doha Lulu Express error log, 7 Aug): the copy loop AWAITS between
  // attachments — if the user opens Reply/New (or closes the compose) mid-loop,
  // _compose is replaced by an object with NO attNotes and the resumed loop
  // crashed on .push. Pin the loop to ITS OWN compose and abandon silently the
  // moment _compose is no longer it — a stale forward must never write into
  // whatever the user is composing now.
  var comp = _compose;
  function noteSkip(a, why) {
    if (_compose !== comp) return;
    comp.attNotes.push({ filename: (a && a.filename) || 'an attachment', why: why });
  }
  for (var i = 0; i < all.length; i++) {
    if (_compose !== comp) return;   // user moved on — stop copying
    var a = all[i];
    if (a.id == null) {
      // Same limitation the read screen states: no fetchable source for this
      // one — the store's normal email has the original.
      noteSkip(a, "can't be copied from this email — attach it from the original file, or forward from the store's normal email");
    } else {
      try {
        var q = '/api/email/attachment?id=' + encodeURIComponent(m.id) + '&att=' + encodeURIComponent(a.id) + (isAccountMode() ? '' : '&' + readIdQ());
        var res = await api(q);
        var d = await res.json().catch(function () { return {}; });
        if (res.ok && d.ok && d.data) {
          var b64 = String(d.data).split(',')[1] || '';
          var size = Math.round((b64.length * 3) / 4);
          var total = comp.atts.reduce(function (s, x) { return s + (x.size || 0); }, 0);
          // Enforce the SEND caps here, not at send time — a copy the server
          // would reject at /api/email/send (5 MB/file, 10 MB together) must
          // not ride along only to fail the whole send later.
          if (size > EM_ATT_FILE_BYTES) noteSkip(a, 'is over the 5 MB send limit');
          else if (total + size > EM_ATT_TOTAL_BYTES) noteSkip(a, 'would take the attachments past 10 MB together');
          else if (comp.atts.length >= EM_ATT_MAX) noteSkip(a, 'is over the ' + EM_ATT_MAX + '-attachment limit');
          else if (_compose === comp) comp.atts.push({ filename: d.filename || a.filename, content_type: d.content_type || a.content_type, content: b64, size: size });
        } else if (res.status === 413) {
          noteSkip(a, 'is too large to copy here (over 8 MB)');
        } else {
          noteSkip(a, 'could not be fetched from the mail provider — try Forward again');
        }
      } catch (e) {
        noteSkip(a, 'could not be fetched — check the connection and try Forward again');
      }
    }
    var box = document.getElementById('emAtts');
    if (box) box.innerHTML = emAttChipsHtml();
    var note = document.getElementById('emAttNote');
    if (note) note.innerHTML = emAttNoteHtml();
  }
  if (_compose !== comp) return;   // user moved on while the last copy was in flight
  comp.attsLoading = false;
  var note2 = document.getElementById('emAttNote');
  if (note2) note2.innerHTML = emAttNoteHtml();
  // One summary toast on top of the persistent note — the note is the record,
  // the toast is the nudge to look at it.
  if (comp.attNotes.length && typeof showToast !== 'undefined') {
    showToast('Could not bring ' + comp.attNotes.length + ' attachment' + (comp.attNotes.length === 1 ? '' : 's') + ' along — see the note in the email', 'error');
  }
}

// ── 📖 the address book (v126.14) — stores + Head Office, searchable, tap to
// add. The Worker builds it from the send_from map + accounts with emails.
var _abook = null;
async function openAddressBook() {
  var old = document.getElementById('emAbook');
  if (old) { old.remove(); return; }
  if (!_abook) {
    try {
      var res = await api('/api/email/addressbook' + (isAccountMode() ? '' : '?' + readIdQ()));
      var d = await res.json();
      if (d && d.ok) _abook = d;
    } catch (e) { /* below */ }
  }
  if (!_abook) { if (typeof showToast !== 'undefined') showToast('Could not load the address book — check the connection', 'error'); return; }
  var modal = document.getElementById('waEmailModal');
  if (!modal) return;
  var card = modal.querySelector('.wa-card');
  var box = document.createElement('div');
  box.id = 'emAbook';
  box.style.cssText = 'position:absolute;inset:0;background:var(--card);z-index:5;display:flex;flex-direction:column;padding:14px;border-radius:14px';
  function rows(list, icon) {
    return list.map(function (e) {
      // v126.58 — data-addr + a ✓ slot so picks can be re-marked in place
      return '<div data-addr="' + esc(e.address) + '" onclick="Chat.pickAddress(\'' + jsArg(e.address) + '\')" style="padding:10px 8px;border-bottom:1px solid var(--surface-3);cursor:pointer;display:flex;gap:8px;align-items:center">' +
        '<span>' + icon + '</span><div style="min-width:0;flex:1" translate="no"><div style="font-weight:700;font-size:13.5px">' + esc(e.name || e.address) + '</div>' +
        '<div style="font-size:11.5px;color:var(--comms-sub);overflow:hidden;text-overflow:ellipsis">' + esc(e.address) + '</div></div>' +
        '<span class="ab-tick" style="color:var(--comms-brand-text);font-weight:900;font-size:15px"></span></div>';
    }).join('');
  }
  box.innerHTML = '<div style="display:flex;gap:8px;align-items:center;margin-bottom:8px">' +
      '<input id="emAbookSearch" type="text" placeholder="Search stores, Head Office and departments…" style="flex:1;border:1px solid var(--border-strong);border-radius:8px;padding:9px;font-size:14px;font-family:inherit" oninput="Chat.filterAddressBook()">' +
      // v126.58 — "Done" rather than a bare ✕: the book now stays open while you
      // add several people, so it needs an obvious way to finish.
      '<button onclick="document.getElementById(\'emAbook\').remove()" style="border:none;background:var(--brand,#009E3D);color:#fff;font-weight:700;border-radius:8px;padding:9px 14px;cursor:pointer;font-family:inherit">Done</button></div>' +
      '<div id="emAbookChosen" style="font-size:12px;color:var(--comms-sub);margin:-2px 0 8px 2px">' + esc(chosenSummary()) + '</div>' +
    '<div id="emAbookList" style="flex:1;overflow-y:auto"></div>';
  card.style.position = 'relative';
  card.appendChild(box);
  window._emAbookRows = rows;   // reused by the filter below
  filterAddressBook();
  var s = document.getElementById('emAbookSearch');
  if (s) s.focus();
}

function filterAddressBook() {
  var listEl = document.getElementById('emAbookList');
  if (!listEl || !_abook) return;
  var q = String((document.getElementById('emAbookSearch') || {}).value || '').trim().toLowerCase();
  var match = function (e) { return !q || (e.name + ' ' + e.address).toLowerCase().indexOf(q) >= 0; };
  var st = (_abook.stores || []).filter(match);
  var pp = (_abook.people || []).filter(match);
  // v129.19 — department groups (Meadows Town Center: one tap for RTV/Finance/
  // IT). Derived from each person's department in admin, so there is no group
  // list to maintain. Rendered HERE, inside the address-book overlay, rather
  // than as a new row on the compose card — that card's flex layout has pushed
  // the Send button off-screen three times (v126.55, v126.82, v129.4).
  var gr = (_abook.groups || []).filter(function (g) {
    if (!q) return true;
    var hay = g.name + ' ' + (g.members || []).map(function (m) { return m.name + ' ' + m.address; }).join(' ');
    return hay.toLowerCase().indexOf(q) >= 0;
  });
  var head = function (t) { return '<div style="font-size:11px;font-weight:800;color:var(--comms-sub);text-transform:uppercase;letter-spacing:.04em;padding:10px 8px 2px">' + t + '</div>'; };
  var groupRows = function (list) {
    return list.map(function (g) {
      var n = (g.members || []).length;
      var who = (g.members || []).map(function (m) { return m.name || m.address; }).join(', ');
      return '<div onclick="Chat.pickGroup(\'' + jsArg(g.name) + '\')" title="' + esc(who) + '" style="padding:10px 8px;border-bottom:1px solid var(--surface-3);cursor:pointer;display:flex;gap:8px;align-items:center">' +
        '<span>👥</span><div style="min-width:0;flex:1"><div style="font-weight:700;font-size:13.5px" translate="no">' + esc(g.name) + '</div>' +
        '<div style="font-size:11.5px;color:var(--comms-sub);overflow:hidden;text-overflow:ellipsis">' + n + ' ' + (n === 1 ? 'person' : 'people') + ' — <span translate="no">' + esc(who) + '</span></div></div>' +
        '<span style="color:var(--comms-sub);font-weight:900;font-size:15px">+</span></div>';
    }).join('');
  };
  listEl.innerHTML =
    (gr.length ? head('👥 Departments') + groupRows(gr) : '') +
    (st.length ? head('🏪 Stores') + window._emAbookRows(st, '🏪') : '') +
    (pp.length ? head('🏢 Head Office') + window._emAbookRows(pp, '👤') : '') +
    (!st.length && !pp.length && !gr.length ? '<div style="padding:20px;text-align:center;color:var(--comms-sub);font-size:13px">No matches</div>' : '');
}

function pickAddress(addr) {
  var to = document.getElementById('emTo');
  if (to) {
    var cur = String(to.value || '').trim();
    if (cur.toLowerCase().indexOf(addr.toLowerCase()) === -1) to.value = cur ? cur.replace(/,\s*$/, '') + ', ' + addr : addr;
  }
  // v126.58 — STAY OPEN. Picking used to close the book, so adding a second
  // person meant reopening 📖 and finding your place again; Arabian Ranches 2
  // reported it as "NO OPTION, ONLY ONE RECEPIENT IS ALLOWED". Multiple
  // recipients always worked — the Worker parses a comma list and allows up to
  // MAX_RECIPIENTS — but nothing in the UI ever said so. Now the list stays up,
  // already-added people show a ✓, and a "Done" button closes it.
  refreshAbookTicks();
  var live = document.getElementById('emAbookChosen');
  if (live) live.textContent = chosenSummary();
}

// v129.19 — add every member of a department in one tap. The 20-recipient cap
// is enforced HERE, before the email is written, rather than by the send route
// after the person has typed the whole thing (the Worker's own limit reports
// "Too many recipients" only at Send). Already-present addresses are not
// re-added, so tapping twice is a no-op rather than an overflow.
function pickGroup(name) {
  var g = (_abook && (_abook.groups || []).filter(function (x) { return x.name === name; })[0]) || null;
  if (!g) return;
  var to = document.getElementById('emTo');
  if (!to) return;
  var addrs = (g.members || []).map(function (m) { return m.address; });
  var res = addGroupToRecipients(to.value, addrs);
  if (!res.ok) {
    if (typeof showToast !== 'undefined') showToast(res.reason, 'error');
    return;
  }
  to.value = res.to;
  if (typeof showToast !== 'undefined') {
    showToast(res.added ? (g.name + ' added — ' + res.added + ' ' + (res.added === 1 ? 'person' : 'people')) : (g.name + ' already added'), 'success');
  }
  refreshAbookTicks();
  var live = document.getElementById('emAbookChosen');
  if (live) live.textContent = chosenSummary();
}

// Who is currently in the To field, lowercased, for the ✓ marks.
function chosenAddrs() {
  var to = document.getElementById('emTo');
  return String((to && to.value) || '').split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
}
function chosenSummary() {
  var n = chosenAddrs().length;
  return n ? (n + ' recipient' + (n === 1 ? '' : 's') + ' added') : 'Tap a name to add them — you can add several';
}
// Re-mark every row without rebuilding the list (a rebuild mid-tap eats the next
// click — the v125.97 lesson).
function refreshAbookTicks() {
  var chosen = chosenAddrs();
  var rows;
  try { rows = document.querySelectorAll('#emAbook [data-addr]'); } catch (e) { return; }
  Array.prototype.forEach.call(rows, function (r) {
    var on = chosen.indexOf(String(r.getAttribute('data-addr') || '').toLowerCase()) !== -1;
    var tick = r.querySelector('.ab-tick');
    if (tick) tick.textContent = on ? '✓' : '';
    r.style.background = on ? 'var(--ui-green-soft)' : '';
  });
}

async function sendEmailNow() {
  var modal = document.getElementById('waEmailModal');
  if (!modal || !_compose) return;
  var to = String(modal.querySelector('#emTo').value || '').trim();
  var subject = String(modal.querySelector('#emSubj').value || '').trim();
  // v126.14: the body is a contenteditable now — innerText is what we store
  // and validate; innerHTML carries the toolbar's bold/italic to the send.
  var bodyEl = modal.querySelector('#emBody');
  var text = String((bodyEl && bodyEl.innerText) || '').trim();
  var errEl = modal.querySelector('#emErr');
  if (!to || !subject || !text) { errEl.textContent = 'Fill in the address, subject and message'; return; }
  // v136.29: a reply carries the earlier email below; something must be typed above it
  if (_compose.replyToId && !text.split('---------- Earlier message ----------')[0].trim()) {
    errEl.textContent = 'Type your reply above the earlier message'; return;
  }
  // v129.69: a quick Send must not race the forward's attachment copies —
  // half the files would silently miss the email (the Mercato report).
  if (_compose.attsLoading) { errEl.textContent = 'Still bringing the original attachments over — try Send again in a moment'; return; }
  // keep the draft across the PIN prompt
  if (to !== _compose.to) _compose.confirmSpelling = false;   // v136.78: a changed address is checked again
  _compose.to = to; _compose.subject = subject; _compose.draft = text;
  _compose.draftHtml = cleanDraftHtml((bodyEl && bodyEl.innerHTML) || '');   // v136.63: the email carries the cleaned html too

  // v125.98 (Matt: "i give the pin, then i cant press send") — a v125.91
  // store-identity till can reach compose with NO picked name, but the send is
  // attributed via _who.id, so it dead-ended silently after the PIN. Same
  // identity-on-demand order as chat's send(): WHO first, then the PIN.
  if (!isAccountMode() && !_who) {
    _pendingAction = function () { renderCompose(); sendEmailNow(); };
    renderWhoPicker();
    return;
  }
  if (!isAccountMode() && !pinFresh()) { askPin(function () { sendEmailNow2(); }); return; }
  sendEmailNow2();
}

async function sendEmailNow2() {
  var modal = document.getElementById('waEmailModal');
  if (!modal) { renderCompose(); modal = document.getElementById('waEmailModal'); if (!modal) return; }
  var errEl = modal.querySelector('#emErr');
  var bodyEl = modal.querySelector('#emBody');
  var payload = {
    to: _compose.to,
    subject: _compose.subject,
    body: _compose.draft || String((bodyEl && bodyEl.innerText) || '').trim(),
    html: _compose.draftHtml || undefined,
    attachments: (_compose.atts && _compose.atts.length)
      ? _compose.atts.map(function (a) { return { filename: a.filename, content_type: a.content_type, content: a.content }; })
      : undefined,
    reply_to_id: _compose.replyToId || undefined,
    confirm_spelling: _compose.confirmSpelling ? true : undefined,   // v136.78: "Send as typed"
  };
  if (isAccountMode()) {
    payload.store = (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : null;
  } else {
    // v125.98: never a silent return — a missing name opens the who-picker
    // and resumes the send after the pick (this was the dead Send button).
    if (!_who) {
      _pendingAction = function () { renderCompose(); sendEmailNow(); };
      renderWhoPicker();
      return;
    }
    payload.auth_staff_id = _who.id;
    payload.auth_pin = _pin;
    _pinAt = Date.now();
    // v131.34: the TILL's store rides along. The server honours it only when
    // it differs from the sender's home store AND the rota places them there
    // (cover) — so a Reem staffer covering at Al Seef sends Al Seef's mail
    // from Al Seef's address, not tereemmall@ (the v131.17 clock principle:
    // attribution follows where the work happens).
    payload.send_as_store = (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : undefined;
  }
  errEl.textContent = 'Sending…';
  try {
    var res = await api('/api/email/send', { method: 'POST', body: JSON.stringify(payload) });
    var d = await res.json().catch(function () { return {}; });
    if (res.status === 401 || res.status === 429) { clearPin(); askPin(function () { sendEmailNow2(); }, (d && d.error) || 'PIN not recognised'); return; }
    if (res.status === 409 && d && Array.isArray(d.typos) && d.typos.length) { showSpellingCheck(errEl, d.typos); return; }
    if (!res.ok) { errEl.textContent = (d && d.error) || ('Could not send (HTTP ' + res.status + ')'); return; }
    closeCompose();
    _emailOpen = null;
    refreshEmails();
  } catch (e) {
    errEl.textContent = 'Could not send — check the connection and try again';
  }
}

// v136.78 (Matt, 29 Oct 2026): the server found an address one slip from a domain mail
// is known to reach (algt-me.co for algt-me.com). Ask, never block: a new supplier's
// domain is unknown to Shift and must still be sendable.
function showSpellingCheck(errEl, typos) {
  _compose.typos = typos;
  var btn = 'border:0;border-radius:8px;padding:8px 12px;font-size:13px;font-weight:700;font-family:inherit;cursor:pointer;margin:6px 6px 0 0';
  errEl.innerHTML = '<div style="text-align:start"><b>' + esc(tr('Check the spelling')) + ':</b>' +
    typos.map(function (w) { return '<div><span translate="no">' + esc(w.address) + '</span>: ' + esc(tr('did you mean')) + ' <b translate="no">' + esc(w.suggest) + '</b>?</div>'; }).join('') +
    '<button type="button" onclick="Chat.emSpellingFix()" style="' + btn + ';background:var(--ui-green-soft);color:var(--chat-accent)">' + esc(tr('Correct and send')) + '</button>' +
    '<button type="button" onclick="Chat.emSpellingKeep()" style="' + btn + ';background:var(--card);color:var(--comms-sub);border:1px solid var(--border-strong)">' + esc(tr('Send as typed')) + '</button></div>';
}

function emSpellingFix() {
  if (!_compose || !_compose.typos) return;
  var to = String(_compose.to || '');
  _compose.typos.forEach(function (w) {
    to = to.split(',').map(function (part) {
      return part.trim().toLowerCase() === String(w.address).toLowerCase() ? (part.match(/^\s*/)[0] + w.suggest) : part;
    }).join(',');
  });
  _compose.to = to;
  _compose.typos = null;
  var el = document.getElementById('emTo');
  if (el) el.value = to;
  sendEmailNow2();
}

function emSpellingKeep() {
  if (!_compose) return;
  _compose.typos = null;
  _compose.confirmSpelling = true;
  sendEmailNow2();
}

function renderList() {
  // v125.41: wide -> the left pane only (an open thread on the right is never
  // touched by a list repaint); narrow -> the whole root, as always.
  var wide = ensureScaffold();
  var el = wide ? paneLeft() : root();
  if (!el) return;
  // v125.47 (Matt: "28 stores + 10 head office people — can they search?") —
  // the search box above the list filters conversations by name, live.
  var q = String(_chatFilter || '').trim().toLowerCase();
  var visible = q ? _channels.filter(function (c) { return chatListTitle(c).toLowerCase().indexOf(q) >= 0; }) : _channels;
  var items = visible.map(function (c) {
    var last = c.last_message;
    // v126.85 — WhatsApp-style "You:" when the last message is the viewer's
    // own (a staff member's report: with the counterparty title fixed, this makes
    // your-turn rows read naturally instead of showing your own full name).
    var senderLabel = last && last.sender_name
      ? (last.sender_name === ((_who && _who.name) || myChatLabel()) ? 'You' : stripStorePrefix(last.sender_name))
      : '';
    // v135.1: the preview line, with the sender's name and the message itself marked translate="no" (the "You",
    // "message removed" and "No messages yet" labels still translate).
    var previewHtml = last
      ? ((senderLabel ? (senderLabel === 'You' ? 'You' : '<span translate="no">' + esc(senderLabel) + '</span>') + ': ' : '') +
        (last.deleted ? 'message removed' : '<span translate="no">' + esc(last.body) + '</span>'))
      : 'No messages yet';
    var name = chatListTitle(c);                          // v125.17: WhatsApp-style — the other party for a DM
    var dmNo = c.kind === 'dm' ? ' translate="no"' : '';   // v135.1: a person's or store's name is never translated
    // Group chats keep an emoji tile; 1:1s get a WhatsApp-style coloured initial.
    var av = c.kind === 'company' ? '<div class="wa-iav" style="background:var(--ui-green-soft)">📢</div>'
      : c.kind === 'ho' ? '<div class="wa-iav" style="background:var(--ui-green-soft)">🏢</div>'             // v125.82
        : c.kind === 'area' ? '<div class="wa-iav" style="background:var(--ui-green-soft)">🗺️</div>'
          : c.kind === 'country' ? '<div class="wa-iav" style="background:var(--ui-green-soft)">🌍</div>'    // v125.66
            : c.kind === 'store' ? '<div class="wa-iav" style="background:var(--ui-green-soft)">🏪</div>'
              : (function () {
                // v128.52 — a DM with a head-office/AM account shows THEIR photo
                // (data-av-* hydrated by the Avatars sweep; initials stay the
                // fallback). Key shape: dm:acct:<id>|store:<name>.
                var am = /(?:^|[|:])acct:(\d+)/.exec(String(c.key || ''));
                var avAttrs = am ? ' data-av-kind="account" data-av-id="' + am[1] + '"' : '';
                return '<div class="wa-iav"' + avAttrs + ' translate="no" style="background:' + chatAvColor(name) + ';color:#fff;overflow:hidden">' + esc(initials(name)) + '</div>';
              })();
    return '<div class="wa-item" role="button" tabindex="0" onclick="Chat.openChannel(\'' + jsArg(c.key) + '\',\'' + jsArg(name) + '\')">' +
      av +
      '<div class="wa-imain"><div class="wa-ititle"' + dmNo + (c.unread > 0 ? ' style="font-weight:800"' : '') + '>' + esc(name) + '</div>' +
      '<div class="wa-ipreview"' + (c.unread > 0 ? ' style="color:var(--text);font-weight:600"' : '') + '>' + previewHtml + '</div></div>' +
      '<div class="wa-imeta">' + (last ? '<div class="wa-itime">' + esc(fmtTime(last.created_at)) + '</div>' : '') +
      (c.muted ? '<div style="font-size:11px;color:var(--comms-mute)">🔕</div>' : '') +   // v125.77
      (c.unread > 0 ? '<div class="wa-iunread">' + (c.unread > 99 ? '99+' : c.unread) + '</div>' : '') + '</div></div>';
  }).join('');
  var onEmail = _commsTab === 'email';
    // v131.79 (Matt: "it does say the staff name when they enter a convo. if it
  // changes, does the name change? if so we should link that to the face.").
  // It does change - this button is whoever last identified themselves on this
  // till, and switchWho() swaps it. So the face has to follow the NAME, not the
  // device: keyed on _who.id, it changes with them. The sweep hydrates it; no
  // photo just leaves the initials circle.
  function whoFaceHtml(w) {
    if (!w || w.id == null || typeof Avatars === 'undefined' || !Avatars.chip) return '';
    return Avatars.chip('staff', w.id, w.name, 16) + ' ';
  }
  var inner = '<div class="wa-wp"></div>' +
    '<div class="wa-head"><div class="wa-av">💬</div>' +
    '<div><div class="wa-hname">Comms</div><div class="wa-hsub">🔒 Visible to Head Office</div></div>' +
    (isAccountMode()
      ? '<span class="wa-who" style="cursor:default" translate="no">' + esc((typeof currentUser !== 'undefined' && currentUser && currentUser.name) || 'Account') + '</span></div>'
      : '<button class="wa-who" onclick="Chat.switchWho()">' + (_who ? whoFaceHtml(_who) + '<span translate="no">' + esc(_who.name) + '</span> · switch' : 'Choose your name') + '</button></div>') +
    commsToggleHtml() +
    (onEmail
      ? commsEmailHtml()
      : (notifBannerHtml() +
        '<div style="padding:8px 10px;background:var(--card);border-bottom:1px solid var(--surface-3)"><input id="waChatSearch" type="text" placeholder="🔍 Search chats" value="' + esc(_chatFilter) + '" oninput="Chat.filterChats(this.value)" style="width:100%;box-sizing:border-box;border:none;background:var(--surface-3);border-radius:18px;padding:8px 14px;font-size:13px;font-family:inherit;outline:none"></div>' +
        '<div class="wa-list">' + jackListEntryHtml() + concernListEntryHtml() + (items || (q ? '<div style="padding:24px;color:var(--comms-sub);font-size:13px;text-align:center">No chats match "' + esc(_chatFilter) + '"</div>' : '<div style="padding:24px;color:var(--comms-sub);font-size:13px;text-align:center">Loading conversations…</div>')) + '</div>' +
        '<button class="wa-fab" onclick="Chat.openNewChat()" title="New conversation">✏️</button>'));
  el.innerHTML = wide ? inner : '<div class="wa-shell">' + inner + '</div>';
  _listSig = channelsSig();   // v125.97: whatever painted the list owns the signature
}
// Switch the Comms sub-tab and re-render (chat state is preserved — only the
// list view swaps for the email placeholder).
// v125.47 — live conversation search. renderList repaints the pane, which
// would blur the input mid-typing, so focus + caret are restored after.
function filterChats(v) {
  _chatFilter = String(v || '');
  renderList();
  var inp = document.getElementById('waChatSearch');
  if (inp) { inp.focus(); try { inp.setSelectionRange(inp.value.length, inp.value.length); } catch (e) {} }
}
function commsTab(t) {
  _commsTab = (t === 'email') ? 'email' : 'chat';
  if (_commsTab === 'chat') _emailOpen = null;   // v125.50: switching back never strands an open email
  // v126.52 — the MIRROR of the line above, which was missing. Switching to
  // Email left _activeKey/_pendingDm/_jackOpen set, and renderRightPane() tests
  // those BEFORE _emailOpen — so on a wide screen the right pane kept showing
  // the old chat thread and clicking an email appeared to do nothing at all.
  // (Arabian Centre, on a desktop PC: "not opening any emails". The fetch was
  // succeeding the whole time — only the render was being hijacked.)
  if (_commsTab === 'email') { _activeKey = null; _pendingDm = null; _jackOpen = false; }
  if (_commsTab === 'email' && _emails === null && !_emailErr) loadEmails(); // first open -> fetch
  renderList();
  if (isWide()) renderRightPane(); // wide: swap the right pane with the tab
}

// ── v129.49 — Jack has a FACE now: the official jack-in-the-box from the TE
// signage lockup (assets/jack-avatar-128.png, extracted from the brand PDF at
// assets/brand-src/TE_signage_logo_JACK.pdf — never redrawn). onerror falls
// back to the 🤖 so a missing asset can never blank the assistant.
function jackAvatarHtml(cls, extraStyle) {
  return '<div class="' + cls + '" style="' + (extraStyle || '') + 'background:var(--card);overflow:hidden">' +
    '<img src="/assets/jack-avatar-128.png" alt="Jack" style="width:100%;height:100%;object-fit:cover" ' +
    'onerror="this.parentNode.textContent=\'🤖\'"></div>';
}

// ── 🤖 Ask Jack — the staff-facing assistant, pinned at the top of the chat list.
// A private Q&A (never posted to humans, never stored — ephemeral this session),
// riding on /api/jack/ask (scoped prompt, rate-limited, tolerant). v125.20.
function jackListEntryHtml() {
  return '<div class="wa-item" role="button" tabindex="0" onclick="Chat.openJack()" style="background:var(--surface-2)">' +
    jackAvatarHtml('wa-iav') +
    '<div class="wa-imain"><div class="wa-ititle">Ask Jack</div>' +
    '<div class="wa-ipreview">Your store assistant — tap to ask a question</div></div>' +
    '<div class="wa-imeta"><div style="font-size:10px;color:#25d366;font-weight:700">AI</div></div></div>';
}
// v125.21 (Matt: is Comms the home for "report a concern"?). We SURFACE it here
// for discoverability — a clearly-confidential entry — but the always-on FAB
// stays (a distressed staffer must reach it from anywhere, not just this tab).
// Its framing is kept distinct from chat's "visible to Head Office": a concern
// is confidential, seen ONLY by Head Office. openConcern() is a global (the FAB
// + login link use it too); guarded so nothing breaks if the bundle is absent.
function concernListEntryHtml() {
  if (isAccountMode()) return '';   // staff-facing channel; managers have their own tools
  return '<div class="wa-item" role="button" tabindex="0" onclick="if(typeof openConcern===\'function\')openConcern()" style="background:var(--comms-red-faint)">' +
    '<div class="wa-iav" style="background:#b91c1c;color:#fff">🛡️</div>' +
    '<div class="wa-imain"><div class="wa-ititle">Report a concern</div>' +
    '<div class="wa-ipreview">Confidential — seen only by Head Office</div></div>' +
    '<div class="wa-imeta"><div style="font-size:10px;color:var(--comms-err);font-weight:700">🔒</div></div></div>';
}
function openJack() {
  // v125.51: Jack logs WHO asked (name + store, into Jack's admin console) —
  // so a name must be picked before the thread opens. Reading chat doesn't
  // need this; Jack does.
  if (!isAccountMode() && !_who) { _pendingAction = openJack; renderWhoPicker(); return; }
  _jackOpen = true; _activeKey = null; _pendingDm = null; renderJack();
}
// v125.44 (Matt: "it should have bold, underline etc rather than show *") —
// Jack's brain answers in markdown; render a SAFE subset instead of the raw
// markers. Order is the security property: esc() runs FIRST, so no text from
// the model (or smuggled through it) can ever inject markup — the transforms
// below only ever ADD <b>/<i> around already-escaped text. Applied to JACK's
// bubbles only; human chat messages stay literal.
function jackMd(text) {
  var s = esc(text);
  // v125.47: links. Both [label](https://url) markdown links and bare URLs
  // become anchors. Safe by construction: the regexes only match http(s)
  // schemes on the ALREADY-ESCAPED string (quotes are &quot; so the href
  // attribute can't be broken out of; javascript: can never match). Link HTML
  // is tokenised first so the later transforms can't mangle URLs.
  var links = [];
  var A = '<a href="$H" target="_blank" rel="noopener" style="color:var(--chat-accent);font-weight:600;text-decoration:underline">$T</a>';
  // Jack's brain embeds document tokens like [FORM:https://...|Leave Request
  // Form] — the standalone Jack app renders them as download buttons, so we
  // do too (must run FIRST or the generic URL rule shreds them). http(s) only.
  s = s.replace(/\[(?:FORM|DOC|FILE|LINK|PDF):(https?:\/\/[^\s|\]]+)\|([^\]\n]+)\]/g, function (m0, href, label) {
    links.push('<a href="' + href + '" target="_blank" rel="noopener" style="display:inline-block;background:#075e54;color:#fff;font-weight:700;font-size:13px;padding:8px 16px;border-radius:18px;text-decoration:none;margin:3px 0">📎 ' + label + '</a>');
    return '\u0001' + (links.length - 1) + '\u0001';
  });
  s = s.replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g, function (m0, label, href) {
    links.push(A.replace('$H', href).replace('$T', label));
    return '\u0001' + (links.length - 1) + '\u0001';
  });
  s = s.replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, function (m0, pre, href) {
    links.push(A.replace('$H', href).replace('$T', href));
    return pre + '\u0001' + (links.length - 1) + '\u0001';
  });
  s = s.replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>');                                  // **bold**
  s = s.replace(/__([^_\n]+)__/g, '<b>$1</b>');                                      // __bold__ (markdown's other marker)
  s = s.replace(/(^|[\s(])\*([^*\n]+)\*(?=$|[\s).,!?:;])/gm, '$1<i>$2</i>');         // *italic* (word-bounded)
  s = s.replace(/^ {0,3}[-*] (?=\S)/gm, '• ');                                  // "- item" -> "• item"
  s = s.replace(/^ {0,3}#{1,4} (.+)$/gm, '<b>$1</b>');                               // "## Heading" -> bold line
  s = s.replace(/\u0001(\d+)\u0001/g, function (m0, i) { return links[Number(i)] || ''; });
  return s;
}
// v125.47: Jack's answers arrive with source documents (sources) and file
// links (fileUrls) — the standalone Jack app shows these as clickable chips,
// so we do too. Only http(s) URLs ever become links; everything is esc()'d.
function jackSourcesHtml(m) {
  var out = [];
  var chip = 'display:inline-block;background:var(--ui-green-soft);color:var(--chat-accent);font-size:12px;font-weight:700;padding:4px 10px;border-radius:12px;text-decoration:none';
  (Array.isArray(m.sources) ? m.sources : []).forEach(function (src) {
    var title = (src && typeof src === 'object') ? (src.title || src.name || src.filename) : (typeof src === 'string' ? src : null);
    var url = (src && typeof src === 'object') ? (src.url || src.link || src.href) : (typeof src === 'string' && /^https?:\/\//.test(src) ? src : null);
    if (url && /^https?:\/\//.test(String(url))) {
      out.push('<a href="' + esc(String(url)) + '" target="_blank" rel="noopener" style="' + chip + '">📄 ' + esc(String(title || 'Source')) + '</a>');
    } else if (title) {
      out.push('<span style="' + chip + ';opacity:.8">📄 ' + esc(String(title)) + '</span>');
    }
  });
  var fu = (m.fileUrls && typeof m.fileUrls === 'object' && !Array.isArray(m.fileUrls)) ? m.fileUrls : {};
  Object.keys(fu).forEach(function (name) {
    var url = String(fu[name] || '');
    if (/^https?:\/\//.test(url)) out.push('<a href="' + esc(url) + '" target="_blank" rel="noopener" style="' + chip + '">📎 ' + esc(name) + '</a>');
  });
  if (!out.length) return '';
  return '<div style="margin-top:7px;display:flex;flex-wrap:wrap;gap:6px">' + out.join('') + '</div>';
}
function jackBubble(m) {
  var mine = m.role === 'user';
  return '<div class="wa-row' + (mine ? ' me' : '') + '"><div class="wa-bubble">' +
    (mine ? '' : '<div class="wa-bname">🤖 Jack</div>') +
    // v135.1: the question someone typed and Jack's own answer are left as they are (translate="no"); a line of
    // an answer that happened to match the catalogue would otherwise come out half-Arabic.
    '<div class="wa-btext" translate="no">' + (mine ? esc(m.text) : jackMd(m.text)) + '</div>' +
    (mine ? '' : jackSourcesHtml(m)) + '</div></div>';
}
function renderJack() {
  ensureStyles();
  // v125.41: wide -> the right pane (list stays put); narrow -> the whole root.
  var wide = ensureScaffold();
  var el = wide ? paneRight() : root();
  if (!el) return;
  var intro = _jackMsgs.length ? '' :
    '<div class="wa-row"><div class="wa-bubble"><div class="wa-bname">🤖 Jack</div>' +
    '<div class="wa-btext">Hi! I\'m Jack, your store assistant. Ask me how something in the app works, or a "how do I…" about your store day. For anything about pay, hours or a colleague, please speak to your manager or Head Office.' +
    '\n\nOne thing to know: questions asked here include your name, so Head Office can follow up if needed. If you\'d rather ask something anonymously, use the Ask Jack website — the link is on the News noticeboard.</div></div></div>';
  var body = intro + _jackMsgs.map(jackBubble).join('') +
    (_jackBusy ? '<div class="wa-row"><div class="wa-bubble"><div class="wa-btext" style="color:var(--sub)">Jack is thinking…</div></div></div>' : '');
  var inner = '<div class="wa-wp"></div>' +
    '<div class="wa-head"><button class="wa-back" aria-label="Back" onclick="Chat.backToList()">←</button>' +
    jackAvatarHtml('wa-av') +
    '<div style="min-width:0"><div class="wa-hname">Jack</div><div class="wa-hsub">Store assistant · asked as you</div></div></div>' +
    '<div class="wa-msgs" id="jackMsgs">' + body + '</div>' +
    '<div class="wa-input">' +
    '<textarea id="jackText" class="wa-text" rows="1" placeholder="Ask Jack…" onkeydown="if(event.key===\'Enter\'&&!event.shiftKey){event.preventDefault();Chat.askJack();}"></textarea>' +
    '<button class="wa-send" onclick="Chat.askJack()" title="Ask">➤</button></div>';
  el.innerHTML = wide ? inner : '<div class="wa-shell wa-full">' + inner + '</div>';   // v127.49: Jack goes full-screen on phones too
  var m = document.getElementById('jackMsgs'); if (m) m.scrollTop = m.scrollHeight;
  var inp = document.getElementById('jackText'); if (inp && !_jackBusy) inp.focus();
}
async function askJack() {
  if (_jackBusy) return;
  var inp = document.getElementById('jackText');
  var q = inp ? String(inp.value || '').trim() : '';
  if (!q) return;
  _jackMsgs.push({ role: 'user', text: q });
  _jackBusy = true;
  renderJack();
  try {
    var myStore = (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : ((_who && _who.store) || null);
    var res = await api('/api/jack/ask', { method: 'POST', body: JSON.stringify({ question: q, history: _jackMsgs.slice(0, -1), userStore: myStore, userName: (_who && _who.name) || _acctName || null, userLang: (typeof I18N !== 'undefined' && I18N.getLang && I18N.getLang() === 'ar') ? 'ar' : 'en' }) });   // v135.1: Jack answers in Arabic on an Arabic device
    var d = res.ok ? await res.json() : null;
    _jackMsgs.push({
      role: 'assistant',
      text: (d && d.answer) || 'Sorry — I couldn\'t answer that just now. Please try again.',
      // v125.47: keep Jack's source-document links for the bubble chips
      sources: (d && d.sources) || null,
      fileUrls: (d && d.fileUrls) || null,
      sig: (d && d.sig) || null,   // v136.63: the Worker's signature, sent back with the history so it counts
    });
  } catch (e) {
    _jackMsgs.push({ role: 'assistant', text: 'Sorry — I couldn\'t reach my brain. Please try again in a moment.' });
  }
  _jackBusy = false;
  renderJack();
}

// The read-identity query: a picked name when there is one, else the signed-in
// store (v125.51 read-before-pick — the Worker validates it against stores).
function readIdQ() {
  if (_who) return 'staff_id=' + encodeURIComponent(_who.id);
  var st = (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : '';
  return st ? 'store=' + encodeURIComponent(st) : '';
}

// v125.97 — signature of the last-RENDERED channel list. The 15s poll used to
// call renderList() on every tick; an innerHTML rebuild mid-tap silently eats
// the click (touch starts on the old node, ends on the new one — no click
// fires). A staff member at Dubai Hills: "moments when you click the email, it gives me
// the chat section" / stuck on Email unable to tap Chat. Now the poll only
// repaints when the list CONTENT actually changed — navigation always renders
// directly, so skipping identical repaints can never leave the pane stale.
var _listSig = null;
function channelsSig() {
  return (_channels || []).map(function (c) {
    return c.key + '|' + (c.unread || 0) + '|' + (c.muted ? 1 : 0) + '|' +
      (c.last ? (c.last.id || '') + ':' + (c.last.created_at || '') + ':' + (c.last.body || '') : '') + '|' + (c.title || '');
  }).join('~');
}

async function refreshChannels() {
  if (!isAccountMode() && !readIdQ()) return;   // no identity of any kind yet
  try {
    // v125.62 (Matt) — an account using the staff-app Comms gets the PERSONAL
    // list (their DMs + company); the full oversight list lives in admin Messages.
    // No-op for staff/store viewers (readIdQ carries their store identity).
    var res = await api(isAccountMode() ? '/api/chat/channels?scope=personal' : '/api/chat/channels?' + readIdQ());
    if (!res.ok) return;
    var d = await res.json();
    if (d && d.ok) {
      _channels = d.channels || [];
      if (d.viewer && d.viewer.kind === 'account') _acctName = d.viewer.name || null;
      updateNavBadge();
      // v125.39 (Matt: "it keeps wanting to go back") — the poll may only
      // repaint the LIST view. The Ask-Jack thread, the new-chat picker, an
      // open email, and a compose modal all live OUTSIDE _activeKey, so
      // without these guards the 15s poll yanked the user back to the list
      // mid-conversation (and wiped whatever they were typing).
      // v125.41: on WIDE screens the list is its own pane — repainting it can't
      // touch the open thread, so only the picker and the compose modal (which
      // hold in-progress typing in the left pane / overlay) block the repaint.
      // v125.97: the EMAIL sub-tab is never the poll's to repaint (it shows
      // emails, not channels — its own refresh button covers it), on BOTH
      // layouts: on wide the left pane hosts the email list too, and rebuilding
      // it ate the Chat/Email toggle taps (a staff member's "stuck on email").
      var listSafe = _commsTab !== 'email' && (isWide()
        ? (!_pickOpen && !_compose)
        : (!_activeKey && !_pendingDm && !_jackOpen && !_pickOpen && !_compose && !_emailOpen));
      // v125.47: never repaint while someone is typing in the search box
      var searching = document.activeElement && document.activeElement.id === 'waChatSearch';
      // v125.97: skip the repaint entirely when nothing visible changed — most
      // ticks change nothing, and every skipped rebuild is a tap that can't be
      // eaten. The badge above is DOM-surgical and already updated either way.
      var sig = channelsSig();
      if (sig !== _listSig && listSafe && !searching && onChatScreen()) renderList();
    }
  } catch (e) { /* poll again later */ }
}

function setNavPill(navId, pillId, n) {
  var nav = document.getElementById(navId);
  if (!nav) return false;
  nav.classList.toggle('nav-attn', n > 0);
  var b = document.getElementById(pillId);
  if (n > 0) {
    if (!b) {
      b = document.createElement('span');
      b.id = pillId;
      b.style.cssText = 'position:absolute;top:2px;inset-inline-end:14px;min-width:16px;height:16px;border-radius:8px;background:var(--brand,#009E3D);color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0 4px';
      nav.style.position = 'relative';
      nav.appendChild(b);
    }
    b.textContent = n > 99 ? '99+' : n;
  } else if (b) { b.remove(); }
  return true;
}

function updateNavBadge() {
  var chatTotal = 0;
  _channels.forEach(function (c) { chatTotal += c.unread || 0; });
  // v128.53 (Stage B): Chat and Email are separate tabs with SEPARATE badges —
  // an email count never hides behind a chat one again. When the split nav
  // isn't present (chat.html standalone), fall back to the combined v127.45
  // pill on nav-chat.
  var split = setNavPill('nav-email', 'emailNavBadge', _emailUnread);
  setNavPill('nav-chat', 'chatNavBadge', split ? chatTotal : chatTotal + _emailUnread);
}

// ── Thread view ───────────────────────────────────────────────────────────────
function openChannel(key, title) { _activeKey = key; _activeTitle = title; _pendingDm = null; renderThread(); }
function backToList() { _activeKey = null; _pendingDm = null; _jackOpen = false; _pickOpen = false; renderThread._last = null; renderChatScreen(); refreshChannels(); }

function threadShell(bodyHtml, subtitle) {
  // v125.41: when wide, the thread renders INSIDE the right pane (no own shell).
  // v127.49: the narrow wrapper carries wa-full — phones get the thread
  // full-screen (see the media query in ensureStyles).
  var inner = threadShellInner(bodyHtml);
  return isWide() ? inner : '<div class="wa-shell wa-full">' + inner + '</div>';
}
// v125.68 (Matt approved: say who can see a chat IN the UI — staff assumed the
// store channel was private). Channel-aware privacy note for the thread header;
// DMs keep the original v125.47 wording.
function threadPrivacyNote() {
  var k = String(_activeKey || '');
  if (k.indexOf('store:') === 0) return '🔒 Everyone at ' + esc(stripStorePrefix(k.slice(6))) + ' + Head Office can see this';
  if (k.indexOf('area:') === 0) return '🔒 The whole area + Head Office can see this';
  if (k.indexOf('country:') === 0) return '🔒 All stores in this country + Head Office can see this';
  if (k === 'company') return '🔒 Everyone in the company can see this';
  if (k === 'ho') return '🔒 Head Office people only';                    // v125.82
  return '🔒 Visible to Head Office';
}
function threadShellInner(bodyHtml) {
  // v125.77 — per-chat mute state feeds the 🔔/🔕 header toggle.
  var mutedNow = false;
  try { var mc = (_channels || []).find(function (c) { return c && c.key === _activeKey; }); mutedNow = !!(mc && mc.muted); } catch (e) {}
  // v129.35 (Matt: "i wonder if we also add the call function to /chat") — 📹
  // turns THIS conversation into a call. The room is derived from the channel
  // key server-side, so the call's audience is the conversation's audience; the
  // Worker gates it with the same canReadChannel that let this thread open.
  // Hidden on a brand-new DM: there is no channel key yet, so there is nothing
  // to derive a room from until the first message creates it.
  var callBtn = _activeKey
    ? '<button id="waCallBtn" title="Start a video call with this conversation" onclick="Chat.startCall()" ' +
      'style="margin-inline-start:auto;background:none;border:none;font-size:18px;cursor:pointer;flex-shrink:0">📹</button>'
    : '';
  var muteBtn = _activeKey
    ? '<button id="waMuteBtn" title="' + (mutedNow ? 'Unmute this chat' : 'Mute this chat') + '" onclick="Chat.toggleMute()" ' +
      'style="' + (callBtn ? '' : 'margin-inline-start:auto;') + 'background:none;border:none;font-size:18px;cursor:pointer;flex-shrink:0">' + (mutedNow ? '🔕' : '🔔') + '</button>'
    : '';
  return '<div class="wa-wp"></div>' +
    '<div class="wa-head"><button class="wa-back" aria-label="Back" onclick="Chat.backToList()">←</button>' +
    '<div class="wa-av" translate="no">' + esc(initials(_activeTitle || '?')) + '</div>' +
    // v135.1: a one-to-one conversation is titled with a person's or a store's name, which is never translated;
    // group titles ("Company", "Head Office") are ours and do translate.
    '<div style="min-width:0"><div class="wa-hname"' + (_activeTitle && (_pendingDm || String(_activeKey || '').indexOf('dm:') === 0) ? ' translate="no"' : '') + '>' + esc(_activeTitle || 'New conversation') + '</div>' +
    // v125.47: WHO is sending is visible inside every thread, and tapping it
    // switches person (Bob leaves, Steve taps his own name in).
    // v135.1: the privacy note sits in its own span so " · sending as" is a separate piece of text to translate.
    '<div class="wa-hsub"><span>' + threadPrivacyNote() + '</span>' +
    (!isAccountMode() && _who ? ' · sending as <span translate="no" onclick="Chat.switchWho()" style="cursor:pointer;text-decoration:underline;font-weight:700">' + esc(_who.name) + '</span>' : '') +
    '</div></div>' + callBtn + muteBtn + '</div>' +
    bodyHtml +
    '<div class="wa-pendbar" id="waPendBar" style="display:none"></div>' +
    '<div class="wa-input">' +
    emojiPopHtml('wa') +
    '<button class="wa-emoji" type="button" title="Emoji" onclick="Chat.toggleEmoji(event)">😊</button>' +
    // v125.93 (Head Office: "attach a PDF or document in chat") — the 📎 used to open
    // a PICTURE picker (accept="image/*"), so documents looked unsupported.
    // Now: 📷 = photo (compressed), 📎 = any allowed file (PDF/Word/Excel…).
    '<button class="wa-attach" title="Attach a photo" onclick="document.getElementById(\'waPhotoIn\').click()">📷</button>' +
    '<button class="wa-attach" title="Attach a document" onclick="document.getElementById(\'waDocIn\').click()">📎</button>' +
    '<button class="wa-attach" title="Send a short video (up to ~1 min; auto-deletes after 7 days)" onclick="document.getElementById(\'waVideoIn\').click()">🎥</button>' +
    // v129.119 — 🎤 voice note (the WhatsApp habit). Tap to record, tap ✓ to
    // preview, send with ➤. Hidden where the browser cannot record.
    (voiceSupported() ? '<button class="wa-attach" id="waMicBtn" title="Record a voice note (up to 90s)" onclick="Chat.startVoice()">🎤</button>' : '') +
    '<input id="waVideoIn" type="file" accept="video/*" style="display:none" onchange="Chat.videoPicked(this)">' +
    '<input id="waPhotoIn" type="file" accept="image/*" style="display:none" onchange="Chat.photoPicked(this)">' +
    '<input id="waDocIn" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*" style="display:none" onchange="Chat.docPicked(this)">' +
    '<textarea id="waText" class="wa-text" rows="1" placeholder="Message" onkeydown="if(event.key===\'Enter\'&&!event.shiftKey){event.preventDefault();Chat.send();}"></textarea>' +
    '<button class="wa-send" id="waSendBtn" onclick="Chat.send()">➤</button></div>';
}

// v125.55 — a lightweight emoji picker (Matt: "can there [be] new emojis in
// Chat"). Self-contained, no library (CSP-clean); inserts at the caret so it
// works mid-message. `ns` = the bundle's class prefix ('wa' here) so the popover
// + textarea ids match the surface it's dropped into.
var CHAT_EMOJIS = ['😀','😁','😂','🤣','😊','😍','😘','😎','🤗','🤔','😅','🙂','😉','😴','😢','😭','😡','🥳','😇','🤩','👍','👎','👌','🙏','👏','🙌','💪','🤝','👋','🔥','✅','❌','⭐','🎉','🎊','❤️','💚','💯','👀','💬','📌','⏰','☕','🛒','🧸','🎈','📦','🚚','🏪','⚠️','📷','🎁','☀️','🌙'];
function emojiPopHtml(ns) {
  return '<div id="' + ns + 'Emoji" class="' + ns + '-emoji-pop" style="display:none">' +
    CHAT_EMOJIS.map(function (e) { return '<button type="button" onclick="Chat.insertEmoji(\'' + e + '\')">' + e + '</button>'; }).join('') +
    '</div>';
}
function toggleEmoji(ev) {
  if (ev && ev.stopPropagation) ev.stopPropagation();
  var p = document.getElementById('waEmoji'); if (!p) return;
  p.style.display = p.style.display === 'none' ? 'flex' : 'none';
}
function insertEmoji(e) {
  var ta = document.getElementById('waText'); if (!ta) return;
  var s = ta.selectionStart != null ? ta.selectionStart : ta.value.length;
  var en = ta.selectionEnd != null ? ta.selectionEnd : ta.value.length;
  ta.value = ta.value.slice(0, s) + e + ta.value.slice(en);
  var pos = s + e.length;
  ta.focus(); try { ta.setSelectionRange(pos, pos); } catch (x) {}
  var p = document.getElementById('waEmoji'); if (p) p.style.display = 'none';
}

// v129.97 — turn an ESCAPED [video:ID:SIG] marker into an inline player.
// Runs AFTER esc(): id is digits, sig is base64url — both survive escaping,
// and the strict character classes mean nothing attacker-shaped can reach
// the attribute we build. Expired videos just 404 into the poster message.
function renderVideoMarkers(escapedHtml) {
  return String(escapedHtml).replace(/\[video:(\d+):([A-Za-z0-9_-]+)\]/g, function (_m, id, sig) {
    var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
    var src = base + '/api/media/video/' + id + '?sig=' + sig;
    return '<video controls preload="metadata" playsinline style="max-width:100%;border-radius:10px;margin-top:4px" src="' + src + '"></video>' +
      '<div style="font-size:10px;color:var(--comms-mute);margin-top:2px">\uD83C\uDFA5 auto-deletes after 7 days</div>';
  });
}

function renderMessages(messages) {
  var out = [];
  var lastDay = '';
  (messages || []).forEach(function (m) {
    var dk = dayKey(m.created_at);
    if (dk && dk !== lastDay) { out.push('<div class="wa-datechip"><span>' + esc(dayLabel(m.created_at)) + '</span></div>'); lastDay = dk; }
    var mine = isAccountMode()
      ? !!(m.sender_kind === 'account' && _acctName && m.sender_name === _acctName)
      : !!(_who && m.sender_kind === 'staff' && Number(m.sender_id) === Number(_who.id));
    if (m.deleted) {
      out.push('<div class="wa-row' + (mine ? ' me' : '') + '"><div class="wa-bubble"><div class="wa-del">🚫 This message was removed</div>' +
        '<div class="wa-btime">' + esc(fmtTime(m.created_at)) + '</div></div></div>');
      return;
    }
    var name = m.sender_name + (m.sender_store ? ' · ' + stripStorePrefix(m.sender_store) : '');
    var photo = m.has_photo
      ? '<div class="wa-photo" data-mid="' + m.id + '">' +
        (_photoCache[m.id] ? '<img src="' + okPhoto(_photoCache[m.id]) + '" onclick="Chat.viewPhoto(this.src)">' : '<div class="wa-photo-ph">📷 loading…</div>') +
        '</div>'
      : '';
    // v129.119 — 🎤 the audio ships on TAP, not with the thread (50 voice notes
    // in a page would be megabytes). The stub shows the duration from metadata.
    var voice = m.has_voice ? voiceStubHtml(m.id, m.voice_duration, 'Chat.playVoice') : '';
    // A6-2: your own message carries ✏️ (within the 15-min window) + ✕ (any time).
    // v125.72: EVERY message carries ↩ (the ops manager: reply to that particular line).
    // v125.86: EVERY message carries 👍 (Matt: "like a post in chat").
    // v125.96: EVERY message carries ↪ Forward (the ops manager: "forward an image
    // from Comms to another user … like reply button").
    var acts = '<div class="wa-mact">' +
      (m.id ? '<button title="Like" onclick="Chat.toggleLike(' + m.id + ')">👍</button>' : '') +
      (m.id ? '<button title="Forward" onclick="Chat.startForward(' + m.id + ',' + (m.has_photo ? 1 : 0) + ',' + (m.has_attachment ? 1 : 0) + ',this)">↪</button>' : '') +
      (m.id ? '<button title="Reply" onclick="Chat.startReply(' + m.id + ',this)">↩</button>' : '');
    if (mine && m.id) {
      var editable = (Date.now() - Date.parse(m.created_at)) < 15 * 60 * 1000;
      acts += (editable ? '<button title="Edit" onclick="Chat.startEdit(' + m.id + ',this)">✏️</button>' : '') +
        '<button title="Remove for everyone" onclick="Chat.deleteMsg(' + m.id + ')">✕</button>';
    }
    acts += '</div>';
    // v125.72: the quoted original (frozen snippet from the server).
    var quote = m.reply
      ? '<div class="wa-quote" translate="no"><b>' + esc(m.reply.sender || '') + '</b><span>' + esc(m.reply.snippet || '') + '</span></div>'
      : '';
    // v125.86: reaction pills (👍 3) — highlighted when one of them is mine.
    // v127.49 (the ops manager: "show WHO liked"): tapping a pill now opens the
    // who-reacted sheet (WhatsApp behaviour) — the wa-mact 👍 button stays the
    // quick like/unlike. Names ride the page in m.reactions[].names.
    if (m.id) _reactCache[m.id] = m.reactions || [];
    var reacts = m.id
      ? '<div class="wa-reacts" id="waR' + m.id + '">' + (m.reactions || []).map(function (r) {
          return '<span class="wa-react' + (r.mine ? ' mine' : '') + '" onclick="Chat.showReactions(' + m.id + ')">' + r.emoji + ' ' + r.count + '</span>';
        }).join('') + '</div>'
      : '';
    // v125.93: the message's document (Head Office: PDFs in chat) — fetched on demand.
    var doc = m.has_attachment
      ? '<div style="margin:2px 0 4px;font-size:13px">📎 <span onclick="Chat.openMsgAttachment(' + m.id + ')" style="color:var(--comms-brand-text);font-weight:700;cursor:pointer;text-decoration:underline">View ' + (m.attachment_name ? '<span translate="no">' + esc(m.attachment_name) + '</span>' : 'attachment') + '</span></div>'
      : '';
    out.push('<div class="wa-row' + (mine ? ' me' : '') + '"><div class="wa-bubble" data-body="' + esc(m.body || '') + '" data-sender="' + esc(m.sender_name || '') + '">' + acts +
      (mine ? '' : '<div class="wa-bname" style="display:flex;align-items:center;gap:5px">' +
        ((m.sender_kind === 'staff' || m.sender_kind === 'account') && m.id && typeof Avatars !== 'undefined' ? Avatars.chip(m.sender_kind, m.sender_id, m.sender_name, 20) : '') +
        '<span translate="no">' + esc(name) + '</span></div>') +
      quote +
      photo +
      voice +
      doc +
      // v135.1: the message is what someone wrote: never translated.
      (m.body ? '<div class="wa-btext" translate="no">' + renderVideoMarkers(esc(m.body)) + '</div>' : '') +
      '<div class="wa-btime">' + esc(fmtTime(m.created_at)) + (m.edited ? ' · edited' : '') + '</div>' + reacts + '</div></div>');
  });
  return out.join('');
}

// v129.99 (9 Aug audit): the 15s poll used to repaint the pane UNCONDITIONALLY,
// which destroyed any playing 🎥 video mid-watch (and re-issued its metadata /
// Range GETs every tick — photos were protected by _photoCache, videos not).
// Repaint only when the message payload actually changed.
var _threadSig = '';

function threadSig(messages) {
  return _activeKey + '|' + JSON.stringify(messages || []);
}

async function renderThread() {
  // v125.41: wide -> the right pane (list stays put); narrow -> the whole root.
  var el = ensureScaffold() ? paneRight() : root();
  if (!el) return;
  if (_pendingDm) {  // brand-new DM — no history yet
    el.innerHTML = threadShell('<div class="wa-msgs" id="waMsgs"><div class="wa-datechip"><span>Say hello 👋 — your first message starts this conversation</span></div></div>');
    return;
  }
  if (!_activeKey) { renderList(); return; }
  el.innerHTML = threadShell('<div class="wa-msgs" id="waMsgs">' + threadPlaceholderHtml() + '</div>');
  try {
    var res = await api('/api/chat/messages?channel=' + encodeURIComponent(_activeKey) + '&' + readIdQ());
    if (!res.ok) throw new Error('status ' + res.status);
    var d = await res.json();
    var pane = document.getElementById('waMsgs');
    if (pane && d && d.ok) {
      pane.innerHTML = renderMessages(d.messages) || '<div class="wa-datechip"><span>No messages yet — say hello 👋</span></div>';
      _threadSig = threadSig(d.messages);
      pane.scrollTop = pane.scrollHeight;
      hydratePhotos(pane);
      // Mark read (cursor is low-stakes; badges only). A store viewer has no
      // per-person cursor to move (v125.51) — skip until a name is picked.
      if (isAccountMode() || _who) {
        api('/api/chat/read', { method: 'POST', body: JSON.stringify(isAccountMode() ? { channel: _activeKey } : { channel: _activeKey, staff_id: _who.id }) }).catch(function () {});
      }
    } else {
      // res.ok but the body said no — this used to leave "Loading…" up forever.
      threadLoadFailed((d && d.error) ? String(d.error) : 'Could not load the messages.');
    }
  } catch (e) {
    threadLoadFailed(/status 40[13]/.test(String(e && e.message))
      ? 'Your session has expired — go back and pick your name again to read this chat.'
      : 'Could not load the messages — check the connection.');
  }
}

// v129.121 (Matt, 13 Aug: "Is there an issue with chat? its not loading") — the
// thread pane opened on a bare "Loading…", and refreshThread below `return`ed
// on !res.ok and swallowed every exception, so an expired identity, a 5xx and a
// dead connection all looked identical to a slow network, forever. The silent
// retry is right — the poll comes round again — but the FIRST paint must never
// keep claiming it is loading when the last attempt failed. Same pair of
// helpers as the Head Office bundle, deliberately: they own their own markup
// (different class prefixes) but must behave the same.
function threadPlaceholderHtml() {
  return '<div id="waLoading" style="padding:16px;color:var(--comms-sub);font-size:13px;text-align:center;z-index:1;position:relative">Loading…</div>';
}

function threadLoadFailed(why) {
  var ph = document.getElementById('waLoading');
  if (!ph) return;                       // real messages are up — retry quietly
  ph.innerHTML = esc(why) +
    '<div style="margin-top:9px"><button onclick="Chat.retryThread()" ' +
    'style="border:1px solid var(--border-strong);background:var(--card);border-radius:14px;padding:6px 14px;font-size:12.5px;cursor:pointer;font-family:inherit">Try again</button></div>';
}

function retryThread() {
  var pane = document.getElementById('waMsgs');
  if (pane) pane.innerHTML = threadPlaceholderHtml();
  return renderThread();
}

// Quiet refresh of the open thread (no full re-render — keeps the composer text).
async function refreshThread() {
  // v129.102: same v125.58 rule here — an account or store viewer with a
  // thread open now live-refreshes it (they previously had to reopen the
  // thread to see new messages; the poll called this but it no-oped).
  if (!_activeKey) return;
  if (!_who && !isAccountMode() && !readIdQ()) return;
  try {
    var res = await api('/api/chat/messages?channel=' + encodeURIComponent(_activeKey) + '&' + readIdQ());
    if (!res.ok) {
      // v129.121: speaks ONLY while the placeholder is still up (see
      // threadLoadFailed) — a poll failure must never wipe a painted thread.
      threadLoadFailed(res.status === 401 || res.status === 403
        ? 'Your session has expired — go back and pick your name again to read this chat.'
        : 'Could not load the messages (error ' + res.status + ').');
      return;
    }
    var d = await res.json();
    var pane = document.getElementById('waMsgs');
    if (pane && d && d.ok) {
      var sig = threadSig(d.messages);
      if (sig === _threadSig) {
        // v129.101 (the ops manager, 9 Aug: "📷 loading..." stuck): an UNCHANGED
        // thread still re-runs photo hydration — a fetch that resolved after
        // its element was replaced cached the photo but painted nothing, and
        // with no repaints nothing retried. hydratePhotos only touches
        // un-hydrated placeholders (cached photos fill instantly; playing
        // videos are never touched), so this is the pre-v129.99 retry loop
        // without the DOM rebuild.
        hydratePhotos(pane);
        return;
      }
      _threadSig = sig;
      var atBottom = pane.scrollHeight - pane.scrollTop - pane.clientHeight < 60;
      pane.innerHTML = renderMessages(d.messages) || '<div class="wa-datechip"><span>No messages yet — say hello 👋</span></div>';
      if (atBottom) pane.scrollTop = pane.scrollHeight;
      hydratePhotos(pane);
    } else {
      threadLoadFailed((d && d.error) ? String(d.error) : 'Could not load the messages.');
    }
  } catch (e) {
    threadLoadFailed('No connection — the messages could not be loaded.');
  }
}

// ── New conversation picker (other stores + Head Office people) ──────────────
async function openNewChat() {
  // v125.41: the picker is a list-side flow — it takes the LEFT pane when wide.
  var wide = ensureScaffold();
  var el = wide ? paneLeft() : root();
  if (!el) return;
  _pickOpen = true;
  var pickInner = '<div class="wa-wp"></div>' +
    '<div class="wa-head"><button class="wa-back" aria-label="Back" onclick="Chat.backToList()" style="display:inline-block">←</button>' +
    '<div><div class="wa-hname">New conversation</div><div class="wa-hsub">Message another store or Head Office</div></div></div>' +
    '<div class="wa-pick" id="waPick"><div style="padding:20px;color:var(--comms-sub);font-size:13px;text-align:center">Loading…</div></div>';
  el.innerHTML = wide ? pickInner : '<div class="wa-shell">' + pickInner + '</div>';
  var stores = [], people = [];
  try {
    var rs = await api('/api/customer-stores');
    var ds = await rs.json();
    stores = (ds && ds.stores) ? ds.stores.map(function (s) { return s.store; }) : [];
  } catch (e) { /* stores unavailable */ }
  try {
    var ra = await api('/api/accounts');
    var da = await ra.json();
    people = (Array.isArray(da) ? da : []).filter(function (a) { return a && a.active !== false; });
  } catch (e) { /* accounts unavailable */ }
  var myStore = (typeof currentStore !== 'undefined') ? currentStore : null;
  var pane = document.getElementById('waPick');
  if (!pane) return;
  var storeRows = stores.filter(function (s) { return s !== myStore; }).map(function (s) {
    return '<div class="wa-item" role="button" tabindex="0" onclick="Chat.startDm(\'store:' + jsArg(s) + '\',\'' + jsArg(s) + '\')">' +
      '<div class="wa-iav">🏪</div><div class="wa-imain"><div class="wa-ititle" translate="no">' + esc(s) + '</div></div></div>';
  }).join('');
  var peopleRows = people.map(function (a) {
    return '<div class="wa-item" role="button" tabindex="0" onclick="Chat.startDm(\'acct:' + a.id + '\',\'' + esc(String(a.name || '').replace(/'/g, '')) + '\')">' +
      '<div class="wa-iav" data-av-kind="account" data-av-id="' + a.id + '" translate="no" style="overflow:hidden">' + esc(initials(a.name)) + '</div><div class="wa-imain"><div class="wa-ititle" translate="no">' + esc(a.name) + '</div>' +
      '<div class="wa-ipreview">' + esc(a.role === 'area_manager' ? 'Area Manager' : 'Head Office') + '</div></div></div>';
  }).join('');
  pane.innerHTML =
    (peopleRows ? '<div class="wa-sect">Head Office &amp; Area Managers</div>' + peopleRows : '') +
    (storeRows ? '<div class="wa-sect">Stores</div>' + storeRows : '') +
    (!peopleRows && !storeRows ? '<div style="padding:20px;color:var(--comms-sub);font-size:13px;text-align:center">Nobody to message yet.</div>' : '');
}

function startDm(participant, title) {
  _pickOpen = false;
  _pendingDm = participant;
  _activeKey = null;
  _activeTitle = title;
  renderThread();
}

// ── Photos (A6): pick → client-compress → preview → ride the send ─────────────
// Compression happens ON THE PHONE (canvas, max 1280px, stepped JPEG quality)
// so a 6MB camera shot becomes a ~100-250KB message — the server hard-caps too.
function compressImage(file, cb) {
  var img = new Image();
  var url = URL.createObjectURL(file);
  img.onload = function () {
    try {
      var scale = Math.min(1, 1280 / Math.max(img.width, img.height));
      var canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      var out = null;
      [0.72, 0.55, 0.4].some(function (q) {
        var d = canvas.toDataURL('image/jpeg', q);
        if (d.length <= 400000) { out = d; return true; }
        out = d; return false;                      // keep last attempt; final check below
      });
      URL.revokeObjectURL(url);
      cb(out && out.length <= 400000 ? out : null);
    } catch (e) { URL.revokeObjectURL(url); cb(null); }
  };
  img.onerror = function () { URL.revokeObjectURL(url); cb(null); };
  img.src = url;
}

function photoPicked(input) {
  var file = input && input.files && input.files[0];
  if (input) input.value = '';                       // allow re-picking the same file
  if (!file) return;
  compressImage(file, function (dataUrl) {
    if (!dataUrl) {
      if (typeof showToast !== 'undefined') showToast('Could not use that photo — try a different one.');
      return;
    }
    _pendingPhoto = dataUrl;
    renderPendingBar();
  });
}

// v125.100 — paste a screenshot straight into the composer (the ops manager: "cut and
// paste directly from Snipping tool instead saved as JPEG and import from
// folder"). One document-level paste listener: when the chat composer is open
// and the clipboard carries an image, it goes through the SAME compress +
// pending pipeline as the 📷 picker — one path, one validator. Text pastes are
// untouched (we only preventDefault when an image file is actually taken).
var _pasteBound = false;
function bindPaste() {
  if (_pasteBound || typeof document === 'undefined') return;
  _pasteBound = true;
  document.addEventListener('paste', function (e) {
    try {
      if (!onChatScreen()) return;
      if (!_activeKey && !_pendingDm) return;              // composer not open
      var items = (e.clipboardData && e.clipboardData.items) || [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].kind === 'file' && /^image\//.test(items[i].type || '')) {
          var f = items[i].getAsFile();
          if (!f) continue;
          e.preventDefault();
          compressImage(f, function (dataUrl) {
            if (!dataUrl) {
              if (typeof showToast !== 'undefined') showToast('Could not use that image — try a different one.');
              return;
            }
            _pendingPhoto = dataUrl;
            renderPendingBar();
            if (typeof showToast !== 'undefined') showToast('📷 Image pasted — press send');
          });
          return;
        }
      }
    } catch (err) { /* a paste must never break typing */ }
  });
}

function renderPendingBar() {
  var bar = document.getElementById('waPendBar');
  if (!bar) return;
  if (_replyTo) {   // v125.72: replying — the quoted line sits above the composer
    bar.style.display = 'flex';
    bar.innerHTML = '<span style="font-size:12px;color:var(--comms-sub);flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' +
      '↩ Replying to <b translate="no">' + esc(_replyTo.sender || '') + '</b>: <span translate="no">' + esc(_replyTo.snippet || '') + '</span>' +
      (_pendingPhoto ? ' · 📷' : '') + (_pendingDoc ? ' · 📎' : '') + (_pendingVoice ? ' · 🎤' : '') + '</span>' +
      '<button class="wa-pendx" aria-label="Cancel reply" onclick="Chat.cancelReply()">✕</button>';
  } else if (_pendingPhoto) {
    bar.style.display = 'flex';
    bar.innerHTML = '<img src="' + _pendingPhoto + '"><span style="font-size:12px;color:var(--comms-sub);flex:1">Photo ready — press send</span>' +
      '<button class="wa-pendx" aria-label="Remove photo" onclick="Chat.clearPendingPhoto()">✕</button>';
  } else if (_pendingDoc) {   // v125.93 — document ready
    bar.style.display = 'flex';
    bar.innerHTML = '<span style="font-size:12px;color:var(--comms-sub);flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">📎 <span translate="no">' + esc(_pendingDoc.filename || 'file') + '</span> — press send</span>' +
      '<button class="wa-pendx" aria-label="Remove file" onclick="Chat.clearPendingDoc()">✕</button>';
  } else if (_rec) {          // v129.119 — recording NOW: red dot + live timer
    bar.style.display = 'flex';
    bar.innerHTML = '<span style="font-size:12px;color:var(--comms-red);font-weight:700;flex:1">● Recording… <span id="waRecT">0:00</span> / 1:30</span>' +
      '<button class="wa-pendx" title="Discard" aria-label="Discard recording" onclick="Chat.cancelVoice()">✕</button>' +
      '<button class="wa-pendx" title="Finish" aria-label="Finish recording" style="color:var(--comms-brand-text);font-weight:800" onclick="Chat.stopVoice()">✓</button>';
  } else if (_pendingVoice) { // v129.119 — recorded: LISTEN BEFORE SENDING
    bar.style.display = 'flex';
    // The preview player is the whole point — nobody should send a pocket
    // recording. src is a blob URL assigned as a PROPERTY below, never markup.
    bar.innerHTML = '<audio controls id="waVoicePrev" style="height:32px;max-width:60%"></audio>' +
      '<span style="font-size:12px;color:var(--comms-sub);flex:1">🎤 ' + fmtDur(_pendingVoice.duration) + ' — press send</span>' +
      '<button class="wa-pendx" aria-label="Remove recording" onclick="Chat.clearPendingVoice()">✕</button>';
    try {
      var pv = document.getElementById('waVoicePrev');
      var dec = decodeDataUrl(_pendingVoice.dataUrl);
      if (pv && dec) pv.src = URL.createObjectURL(new Blob([dec.bytes], { type: dec.mime }));
    } catch (e) { /* preview is best-effort — send still works */ }
  } else {
    bar.style.display = 'none';
    bar.innerHTML = '';
  }
}
function clearPendingPhoto() { _pendingPhoto = null; renderPendingBar(); }
function clearPendingVoice() { _pendingVoice = null; renderPendingBar(); }

// ── v129.119/121 — 🎤 voice notes. The recorder lives in
// src/shared/voice-note.js so this app and the Head Office app share ONE
// implementation; what stays here is only this app's markup + state.
function fmtDur(s) { return vnFmtDur(s); }
async function startVoice() {
  if (_pendingVoice) return;                    // clear the pending one first
  await startRecording({
    onTick: function (secs) {
      var el = document.getElementById('waRecT');
      if (el) el.textContent = vnFmtDur(secs);
    },
    onDone: function (v) { _pendingVoice = v; _rec = null; renderPendingBar(); },
    onCancel: function () { _rec = null; renderPendingBar(); },
    onError: function (m) { _rec = null; renderPendingBar(); if (typeof showToast !== 'undefined') showToast(m, 'error'); },
  }) && (_rec = true, renderPendingBar());
}
function stopVoice() { stopRecording(); }
function cancelVoice() { cancelRecording(); }

// Play a received voice note — the audio is fetched ON TAP, never with the
// thread, then swapped in as a real player (shared, so both apps behave alike).
function playVoice(id, el) {
  return playVoiceInto(id, el, async function (mid) {
    var res = await api('/api/chat/voice?id=' + encodeURIComponent(mid) + '&' + readIdQ());
    var d = res.ok ? await res.json() : null;
    return d && d.ok ? d.voice : null;
  });
}

// ── v125.93 — document attach (Head Office: "attach a PDF or document in chat") ────
// The v154 tasks pattern: FileReader → bounded data URL; the Worker validates
// (allow-listed types, 6MB) and stores it in chat_message_attachments (v160).
function chatResolveMime(f) {
  var allow = ['image/png','image/jpeg','image/jpg','image/webp','image/gif','application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document','application/vnd.ms-excel','application/vnd.openxmlformats-officedocument.spreadsheetml.sheet','text/plain','text/csv'];
  var t = String((f && f.type) || '').toLowerCase();
  if (allow.indexOf(t) >= 0) return t;
  var ext = String((f && f.name) || '').split('.').pop().toLowerCase();
  var map = { png:'image/png', jpg:'image/jpeg', jpeg:'image/jpeg', webp:'image/webp', gif:'image/gif', pdf:'application/pdf', doc:'application/msword', docx:'application/vnd.openxmlformats-officedocument.wordprocessingml.document', xls:'application/vnd.ms-excel', xlsx:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', csv:'text/csv', txt:'text/plain' };
  return map[ext] || t || 'application/octet-stream';
}
function docPicked(input) {
  var f = input && input.files && input.files[0];
  if (input) input.value = '';                       // allow re-picking the same file
  if (!f) return;
  // v129.9 (Matt: "worried about someone sending a super high def photo") —
  // the 📎 picker accepts image/* too, and images were riding RAW up to 6MB
  // while the 📷 path compressed to ≤400KB. That asymmetry is why
  // chat_message_attachments averaged ~1.5MB/row (146MB in 96 rows on prod).
  // An image picked via 📎 now goes through the SAME compressor as 📷 —
  // whichever button staff tap, a photo costs ≤400KB. Documents stay raw.
  if (/^image\//.test(f.type || '')) {
    compressImage(f, function (dataUrl) {
      if (!dataUrl) { if (typeof showToast !== 'undefined') showToast('Could not read that photo — try the 📷 button.'); return; }
      var name = String(f.name || 'photo').replace(/\.[a-z0-9]+$/i, '') + '.jpg';
      _pendingDoc = { filename: name, content_type: 'image/jpeg', data: dataUrl };
      renderPendingBar();
    });
    return;
  }
  if (f.size > 6 * 1024 * 1024) { if (typeof showToast !== 'undefined') showToast('That file is too large (max 6 MB).'); return; }
  var reader = new FileReader();
  reader.onload = function () {
    var mime = chatResolveMime(f);
    var b64 = String(reader.result || '').split(',')[1] || '';
    _pendingDoc = { filename: f.name, content_type: mime, data: 'data:' + mime + ';base64,' + b64 };
    renderPendingBar();
  };
  reader.onerror = function () { if (typeof showToast !== 'undefined') showToast('Could not read that file — try again.'); };
  reader.readAsDataURL(f);
}
function clearPendingDoc() { _pendingDoc = null; renderPendingBar(); }
// The message's document, fetched on demand and opened with the v125.81
// iOS-safe opener (data:-URL anchors are silent no-ops in installed PWAs).
async function openMsgAttachment(id) {
  try {
    var res = await api('/api/chat/attachment?id=' + encodeURIComponent(id) + (isAccountMode() ? '' : '&' + readIdQ()));
    if (!res.ok) { if (typeof showToast !== 'undefined') showToast('Could not open the attachment'); return; }
    var d = await res.json();
    if (d && d.ok && d.data) openChatDataUrl(d.data, d.filename, d.content_type);
  } catch (e) { if (typeof showToast !== 'undefined') showToast('Could not open the attachment'); }
}
function openChatDataUrl(dataUrl, filename, contentType) {
  // v129.122 — this WAS a hand-rolled fourth copy of the opener, written before
  // v129.112 consolidated them and missed by that sweep because it lives inside
  // a chat module rather than an attachment one. It carried the same
  // media-type-parameter bug that broke voice playback, and it string-built the
  // image into innerHTML. Both are gone: one opener, one decoder.
  openStoredFile(dataUrl, filename, contentType);
}

// Fetch each has_photo bubble's image on demand (threads stay light) + cache.
function hydratePhotos(pane) {
  // v129.102 (the ops manager still stuck after v129.101): the v125.58 rule — refresh
  // for ANY read identity — never reached this function. Accounts (Bearer) and
  // store viewers (no picked name) could always FETCH photos (the endpoint
  // gates on channel access), but this guard blocked hydration entirely.
  if (!pane) return;
  if (!_who && !isAccountMode() && !readIdQ()) return;
  pane.querySelectorAll('.wa-photo[data-mid]').forEach(function (el) {
    var id = el.getAttribute('data-mid');
    if (!id || el.querySelector('img')) return;
    if (_photoCache[id]) { el.innerHTML = '<img src="' + okPhoto(_photoCache[id]) + '" onclick="Chat.viewPhoto(this.src)">'; return; }
    api('/api/chat/photo?id=' + encodeURIComponent(id) + '&' + readIdQ())
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (d && d.ok && d.photo) {
          _photoCache[id] = okPhoto(d.photo);
          if (el.isConnected) el.innerHTML = '<img src="' + _photoCache[id] + '" onclick="Chat.viewPhoto(this.src)">';
        } else if (el.isConnected) { el.innerHTML = '<div class="wa-photo-ph">📷 unavailable</div>'; }
      })
      .catch(function () { if (el.isConnected) el.innerHTML = '<div class="wa-photo-ph">📷 unavailable</div>'; });
  });
}

// Full-screen viewer — tap anywhere to close (the WhatsApp gesture).
function viewPhoto(src) {
  var v = document.createElement('div');
  v.className = 'wa-viewer';
  v.innerHTML = '<img src="' + okPhoto(src) + '">';
  v.addEventListener('click', function () { v.remove(); });
  document.body.appendChild(v);
}

// ── Per-chat mute (v125.77) ───────────────────────────────────────────────────
// Matt: "THE OPTION TO MUTE INDIVIDUAL CHATS. i want it to act like whats app."
// Stops this chat's push notifications ONLY — messages, unread badges and
// history keep flowing. Staff mute at TILL level (shared device, shared mute).
// v125.86 (Matt: "like a post in chat") — toggle my 👍 on one message and
// update the pill in place from the server's post-change count (the poll
// reconciles everyone else's). Identity mirrors mute: account = personal,
// till = store-level (needs a picked name for attribution on the staff app).
// ── v127.49 — the who-reacted sheet (the ops manager: reactions should "show WHO
// liked"). Tapping a pill opens it (WhatsApp behaviour); the wa-mact 👍 stays
// the quick like. Names arrive with the page (reactionSummary carries up to
// 40 per emoji; the count is always exact). The 👍 button inside the sheet
// toggles your own like and closes so the refreshed pills stay honest.
function showReactions(id) {
  var list = _reactCache[id] || [];
  if (!list.length) { toggleLike(id); return; }   // no pills yet — treat the tap as a quick like
  closeReactions();
  var sheet = document.createElement('div');
  sheet.id = 'waReactSheet';
  sheet.style.cssText = 'position:fixed;inset:0;background:var(--overlay);z-index:99998;display:flex;align-items:flex-end;justify-content:center';
  var rows = list.map(function (r) {
    var names = r.names || [];
    var more = r.count > names.length ? '<div style="font-size:12px;color:var(--comms-sub);padding:2px 0">+ ' + (r.count - names.length) + ' more</div>' : '';
    return '<div style="padding:10px 14px;border-bottom:1px solid var(--surface-3)">' +
      '<div style="font-weight:800;font-size:13px;color:var(--text);margin-bottom:4px">' + r.emoji + ' ' + r.count + '</div>' +
      names.map(function (n) { return '<div style="font-size:13px;color:var(--text);padding:2px 0" translate="no">' + esc(n) + '</div>'; }).join('') + more +
      '</div>';
  }).join('');
  // v129.45 (social layer #2) — four quick reactions instead of one Like.
  // The server accepted six emoji since v125.86 but the sheet only ever
  // offered 👍, so nobody could 👏 a badge announcement — the celebration set
  // existed on the wire and not on the screen. Emoji sit in QUICK_REACTS and
  // are indexed into the onclick (never interpolated as text into the JS
  // string — the injection rule, even for our own constants).
  var quickBtns = QUICK_REACTS.map(function (em, qi) {
    var mineEm = list.some(function (r) { return r.emoji === em && r.mine; });
    return '<button onclick="Chat.quickReact(' + id + ',' + qi + ')" style="border:1px solid ' + (mineEm ? 'var(--brand,#009E3D)' : 'var(--border-strong)') + ';background:' + (mineEm ? 'var(--chat-react-mine)' : 'var(--card)') + ';font-size:15px;cursor:pointer;border-radius:10px;padding:4px 9px">' + em + '</button>';
  }).join('');
  sheet.innerHTML = '<div style="background:var(--card);width:100%;max-width:440px;max-height:70vh;border-radius:14px 14px 0 0;overflow:hidden;display:flex;flex-direction:column">' +
    '<div style="display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border-bottom:1px solid var(--border);gap:8px">' +
      '<div style="font-weight:800;font-size:14px;color:var(--text)">Reactions</div>' +
      '<div style="display:flex;gap:6px;align-items:center">' + quickBtns +
      '<button onclick="Chat.closeReactions()" style="border:none;background:transparent;font-size:16px;cursor:pointer;color:var(--comms-sub)">✕</button></div></div>' +
    '<div style="overflow-y:auto">' + rows + '</div></div>';
  sheet.addEventListener('click', function (e) { if (e.target === sheet) closeReactions(); });
  document.body.appendChild(sheet);
}
function closeReactions() {
  var s = document.getElementById('waReactSheet');
  if (s) s.remove();
}

// The sheet's quick row. A subset of the server's REACTION_EMOJI — enough to
// like, love, applaud and celebrate without a full picker.
var QUICK_REACTS = ['👍', '❤️', '👏', '🎉'];
function quickReact(id, qi) {
  var em = QUICK_REACTS[qi];
  if (em) { toggleLike(id, em); closeReactions(); }
}

async function toggleLike(id, emoji) {
  emoji = emoji || '👍';
  if (!isAccountMode() && !_who) { if (typeof showToast !== 'undefined') showToast('Choose your name first'); return; }
  var rowEl = document.getElementById('waR' + id);
  var pill = null;
  if (rowEl) {
    for (var i = 0; i < rowEl.children.length; i++) {
      if (rowEl.children[i].textContent.indexOf(emoji) === 0) { pill = rowEl.children[i]; break; }
    }
  }
  var had = !!(pill && pill.className.indexOf('mine') !== -1);
  try {
    var payload = { message_id: id, emoji: emoji, liked: !had };
    if (!isAccountMode() && _who) payload.staff_id = _who.id;
    var res = await api('/api/chat/react', { method: 'POST', body: JSON.stringify(payload) });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || d.ok === false) {
      if (typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not react');
      return;
    }
    if (rowEl) {
      if (d.count > 0) {
        if (!pill) {
          pill = document.createElement('span');
          pill.onclick = function () { toggleLike(id, emoji); };
          rowEl.appendChild(pill);
        }
        pill.className = 'wa-react' + (d.liked ? ' mine' : '');
        pill.textContent = emoji + ' ' + d.count;
      } else if (pill) { pill.remove(); }
    }
  } catch (e) { if (typeof showToast !== 'undefined') showToast('No connection — try again'); }
}

async function toggleMute() {
  if (!_activeKey) return;
  var c = (_channels || []).find(function (x) { return x && x.key === _activeKey; });
  var next = !(c && c.muted);
  try {
    var payload = { channel: _activeKey, muted: next };
    if (!isAccountMode() && _who) payload.staff_id = _who.id;
    var res = await api('/api/chat/mute', { method: 'POST', body: JSON.stringify(payload) });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || d.ok === false) {
      if (typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not change mute');
      return;
    }
    if (c) c.muted = next;
    var btn = document.getElementById('waMuteBtn');
    if (btn) { btn.textContent = next ? '🔕' : '🔔'; btn.title = next ? 'Unmute this chat' : 'Mute this chat'; }
    if (typeof showToast !== 'undefined') showToast(next ? '🔕 Muted — no more notifications from this chat' : '🔔 Unmuted');
  } catch (e) {
    if (typeof showToast !== 'undefined') showToast('Could not change mute — check the connection');
  }
}

// ── 📹 Turn this conversation into a call (v129.35) ──────────────────────────
// Matt, 3 Aug: "i wonder if we also add the call function to /chat".
//
// The Worker derives the room from the channel key and gates it with the same
// canReadChannel that opened this thread, so the call's audience is exactly the
// conversation's audience. Nothing is stored — no invite row, no room record.
//
// The join itself is handed to the Calls bundle rather than reimplemented here:
// one call overlay, one JaaS token path, one place where the low-bandwidth
// quality default lives. If Calls has not loaded (an old cached client, or the
// bundle failed), say so plainly instead of half-opening something.
async function startCall() {
  if (!_activeKey) return;
  if (typeof Calls === 'undefined' || !Calls.joinFromResponse) {
    if (typeof showToast !== 'undefined') showToast('Calls are not available on this device yet — reload the app');
    return;
  }
  // v129.120 (Matt, 13 Aug: "make a note when they use voice call to say
  // 'ensure the store are expecting your call'"). The tap RINGS the other
  // till's whole screen, so one confirm with the etiquette line — placed on the
  // caller, who is the only one who can do anything about it.
  try {
    if (typeof window !== 'undefined' && typeof window.confirm === 'function' &&
        !window.confirm('Start a call in ' + (_activeTitle || 'this conversation') + '?\n\nMake sure the store is expecting your call — drop them a message here first if not.')) {
      return;
    }
  } catch (e) { /* no confirm available — proceed */ }
  try {
    // v129.38 — POST, not GET: this RINGS the other side (a call_invites row +
    // a push), and a write must not hang off a GET that a retry or a prefetch
    // could repeat. The Worker decides WHO to ring from the channel key; a
    // company-wide channel deliberately opens without ringing 28 stores.
    var res = await api('/api/chat/call?channel=' + encodeURIComponent(_activeKey) + '&' + readIdQ(),
      { method: 'POST', body: JSON.stringify({}) });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || !d || !d.ok || !d.room) {
      if (typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not start a call for this conversation');
      return;
    }
    // Say what happened either way. "It rang them" and "it opened but did not
    // ring" look identical from the caller's side otherwise, and the second one
    // needs them to go and say so in the thread.
    if (typeof showToast !== 'undefined') {
      if (d.ring && d.ring.rang) showToast('📞 Ringing ' + d.ring.to + '…');
      else if (d.ring && d.ring.note) showToast(d.ring.note);
    }
    Calls.joinFromResponse(d, _activeTitle || 'Call', { kind: 'chat', target: _activeKey });
  } catch (e) {
    if (typeof showToast !== 'undefined') showToast('Could not start the call — check the connection');
  }
}

// ── Quote-reply (v125.72) ─────────────────────────────────────────────────────
// The ops manager: "likewise in whatsapp, i want to respond to that particular line."
var _replyTo = null;   // { id, sender, snippet } while composing a reply

function startReply(id, btn) {
  var bubble = btn && btn.closest ? btn.closest('.wa-bubble') : null;
  var body = (bubble && bubble.getAttribute('data-body')) || '';
  var sender = (bubble && bubble.getAttribute('data-sender')) || '';
  _replyTo = { id: id, sender: sender, snippet: body.length > 90 ? body.slice(0, 89) + '…' : body };
  _editingId = null;
  renderPendingBar();
  var ta = document.getElementById('waText');
  if (ta) ta.focus();
}
function cancelReply() { _replyTo = null; renderPendingBar(); }

// ── Edit / delete own (A6-2) ──────────────────────────────────────────────────
var _editingId = null;

function startEdit(id, btn) {
  clearPendingPhoto();
  _replyTo = null;                 // v125.72: editing and replying are exclusive
  _editingId = id;
  var bubble = btn && btn.closest ? btn.closest('.wa-bubble') : null;
  var ta = document.getElementById('waText');
  if (ta) { ta.value = (bubble && bubble.getAttribute('data-body')) || ''; ta.focus(); }
  var bar = document.getElementById('waPendBar');
  if (bar) {
    bar.style.display = 'flex';
    bar.innerHTML = '<span style="font-size:12px;color:var(--chat-accent);font-weight:700;flex:1">✏️ Editing message — send to save</span>' +
      '<button class="wa-pendx" aria-label="Cancel editing" onclick="Chat.cancelEdit()">✕</button>';
  }
}
function cancelEdit() {
  _editingId = null;
  var ta = document.getElementById('waText');
  if (ta) ta.value = '';
  renderPendingBar();
}

async function deleteMsg(id) {
  // v136.8: the Shift dialog in the app's language, not the browser's English confirm(). Same answer: Remove goes ahead.
  if (!(await askConfirm(tr('Remove this message for everyone?') + '\n\n' + tr('It stays on record for Head Office.'), { danger: true, confirmLabel: tr('Remove') }))) return;
  if (!isAccountMode() && !pinFresh()) { askPin(function () { deleteMsg2(id); }); return; }
  deleteMsg2(id);
}
async function deleteMsg2(id) {
  try {
    var res = await api('/api/chat/delete', { method: 'POST', body: JSON.stringify(isAccountMode() ? { id: id } : { id: id, auth_staff_id: _who.id, auth_pin: _pin }) });
    var d = await res.json().catch(function () { return {}; });
    if (res.status === 401 || res.status === 429) { clearPin(); askPin(function () { deleteMsg2(id); }, (d && d.error) || 'PIN not recognised'); return; }
    if (!res.ok && typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not remove');
    else _pinAt = Date.now();
  } catch (e) { /* refresh shows the truth */ }
  refreshThread();
}

// ── Sending (PIN-attributed) ──────────────────────────────────────────────────
function send() {
  var ta = document.getElementById('waText');
  var text = ta ? ta.value : '';
  if ((!String(text || '').trim() && !_pendingPhoto && !_pendingDoc && !_pendingVoice) || _sending) return;  // photo/doc/voice-only OK
  if (isAccountMode()) { if (_editingId) { doEdit(text); } else { doSend(text); } return; }
  if (!_who) {
    // v125.51: identity on demand — keep the typed message through the pick.
    var typed = text;
    _pendingAction = function () {
      var ta2 = document.getElementById('waText');
      if (ta2 && !ta2.value) ta2.value = typed;
      send();
    };
    renderWhoPicker();
    return;
  }
  if (!pinFresh()) { askPin(function () { send(); }); return; }
  if (_editingId) { doEdit(text); return; }
  doSend(text);
}

async function doEdit(text) {
  var ta = document.getElementById('waText');
  _sending = true;
  try {
    var res = await api('/api/chat/edit', { method: 'POST', body: JSON.stringify(isAccountMode() ? { id: _editingId, body: text } : { id: _editingId, body: text, auth_staff_id: _who.id, auth_pin: _pin }) });
    var d = await res.json().catch(function () { return {}; });
    if (res.status === 401 || res.status === 429) { clearPin(); askPin(function () { send(); }, (d && d.error) || 'PIN not recognised'); return; }
    if (!res.ok || d.ok === false) {
      if (typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not edit the message');
      return;
    }
    _pinAt = Date.now();
    cancelEdit();
    if (ta) ta.value = '';
    await refreshThread();
  } catch (e) {
    if (typeof showToast !== 'undefined') showToast('Could not edit — check the connection');
  } finally { _sending = false; }
}

// v129.97 — 🎥 chat video (Matt 9 Aug: allowed, auto-deletes after 7 days).
// The bytes go to R2 via the Worker (intent -> signed PUT); the message body
// carries a [video:ID:SIG] marker the renderer turns into an inline player.
// A store manager's "space to big" wall was the ~300KB photo cap; videos get 50MB.
async function videoPicked(input) {
  var file = input && input.files && input.files[0];
  if (input) input.value = '';
  if (!file) return;
  if (file.size > 50 * 1024 * 1024) {
    alert(tr('That video is too big (max 50 MB \u2014 about a minute). Trim it and try again.'));   // v136.8: in the app's language
    return;
  }
  if (!isAccountMode() && !_who) { renderWhoPicker(); return; }
  var btn = document.getElementById('waSendBtn');
  var prev = btn ? btn.textContent : null;
  if (btn) { btn.disabled = true; btn.textContent = '\u23F3'; }
  try {
    var authBody = isAccountMode() ? {} : { auth_staff_id: _who.id, auth_pin: _pin };
    // NOTE (v129.99 audit): we deliberately do NOT send `seconds` — measuring
    // duration client-side needs a decode pass that is flaky on low-end store
    // phones. The 50MB byte cap is the enforced limit; the server's 90s check
    // only fires for callers that do send a duration.
    var ir = await api('/api/media/video-intent', { method: 'POST',
      body: JSON.stringify(Object.assign({ bytes: file.size }, authBody)) });
    var intent = await ir.json().catch(function () { return {}; });
    if (!ir.ok || !intent.ok) { alert(tr(intent.error || 'Could not start the video upload.')); return; }
    var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
    var pr = await fetch(base + intent.put, { method: 'PUT', cache: 'no-store',
      headers: { 'Content-Type': file.type || 'video/webm' }, body: file });
    var pd = await pr.json().catch(function () { return {}; });
    if (!pr.ok || !pd.ok) { alert(tr(pd.error || 'The video upload failed \u2014 try again on better wifi.')); return; }
    await doSend('\uD83C\uDFA5 [video:' + intent.id + ':' + intent.sig + ']');
  } catch (e) {
    alert(tr('The video upload failed \u2014 try again on better wifi.'));
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = prev || '\u27A4'; }
  }
}

async function doSend(text) {
  var ta = document.getElementById('waText');
  var btn = document.getElementById('waSendBtn');
  _sending = true;
  if (btn) btn.disabled = true;
  // v125.75 (Matt: "quite the lag") — WhatsApp-style optimistic bubble: the
  // message renders + the composer clears IMMEDIATELY; on success refreshThread
  // replaces the pane (reconciling the real row), and EVERY failure path
  // removes the bubble + restores the draft so nothing is silently lost.
  // Photo sends keep the plain flow (the preview bar is their feedback).
  var optEl = null;
  var draft = text;
  var pane0 = document.getElementById('waMsgs');
  if (pane0 && !_pendingPhoto && !_pendingDoc && !_pendingVoice && String(text || '').trim()) {
    optEl = document.createElement('div');
    optEl.className = 'wa-row me';
    optEl.innerHTML = '<div class="wa-bubble">' +
      (_replyTo ? '<div class="wa-quote" translate="no"><b>' + esc(_replyTo.sender || '') + '</b><span>' + esc(_replyTo.snippet || '') + '</span></div>' : '') +
      '<div class="wa-btext" translate="no">' + esc(draft) + '</div><div class="wa-btime">🕓 sending…</div></div>';
    pane0.appendChild(optEl);
    pane0.scrollTop = pane0.scrollHeight;
    if (ta) ta.value = '';
  }
  var unsend = function () {
    if (optEl) { try { optEl.remove(); } catch (e2) {} optEl = null; }
    if (ta && !ta.value) ta.value = draft;
  };
  try {
    var payload = isAccountMode() ? { body: text } : { body: text, auth_staff_id: _who.id, auth_pin: _pin };
    if (_pendingPhoto) payload.photo = _pendingPhoto;
    if (_pendingDoc) payload.attachment = _pendingDoc;                   // v125.93 document
    if (_pendingVoice) { payload.voice = _pendingVoice.dataUrl; payload.voice_duration = _pendingVoice.duration; }   // v129.119 🎤
    if (_replyTo) payload.reply_to = _replyTo.id;                        // v125.72 quote-reply
    if (_activeKey) payload.channel = _activeKey; else if (_pendingDm) payload.dm_with = _pendingDm;
    var res = await api('/api/chat/send', { method: 'POST', body: JSON.stringify(payload) });
    var d = await res.json().catch(function () { return {}; });
    if (res.status === 401 || res.status === 429) {
      unsend();
      clearPin();
      askPin(function () { send(); }, (d && d.error) || 'PIN not recognised — try again');
      return;
    }
    if (!res.ok || d.ok === false) {
      unsend();
      if (typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not send the message');
      return;
    }
    _pinAt = Date.now();                       // keep the PIN warm while they chat
    if (_pendingDm && d.channel) { _activeKey = d.channel; _pendingDm = null; }
    if (ta) ta.value = '';
    _replyTo = null;                           // v125.72: the reply went with the send
    // v125.93: the message saved but the FILE didn't (v160 not run on this
    // environment) — say so loudly, the v125.89 posture.
    if (_pendingDoc && d.attachment_saved === false && typeof showToast !== 'undefined') {
      showToast('Sent — but the file was NOT attached. Run migrations/v160-0-chat-message-attachments.sql on this environment.');
    }
    // v129.119 — a bubble that plays silence is worse than an honest error.
    if (_pendingVoice && d.voice_saved === false && typeof showToast !== 'undefined') {
      showToast('Sent — but the voice note was NOT saved. Run migrations/v197-0-chat-voice.sql on this environment.');
    }
    clearPendingPhoto();
    clearPendingDoc();
    clearPendingVoice();
    // The bubble is confirmed even if the background refresh below hiccups.
    if (optEl) { var tEl = optEl.querySelector('.wa-btime'); if (tEl) tEl.textContent = fmtTime(new Date().toISOString()); }
    await refreshThread();
    var pane = document.getElementById('waMsgs');
    if (pane) pane.scrollTop = pane.scrollHeight;
  } catch (e) {
    unsend();
    if (typeof showToast !== 'undefined') showToast('Could not send — check the connection');
  } finally {
    _sending = false;
    if (btn) btn.disabled = false;
  }
}

// The PIN prompt — same job as the till PIN pad: prove who's speaking. The PIN
// lives in memory only and goes stale after 5 idle minutes.
function askPin(then, errMsg) {
  var el = root(); if (!el) return;
  var old = document.getElementById('waPinModal');
  if (old) old.remove();
  var wrap = document.createElement('div');
  wrap.id = 'waPinModal';
  wrap.className = 'wa-modal';
  wrap.innerHTML = '<div class="wa-card">' +
    '<div style="font-weight:700;font-size:15px;color:var(--text)"><span translate="no">' + esc(_who ? _who.name : '') + '</span> — confirm it\'s you</div>' +
    '<div style="font-size:12px;color:var(--comms-sub);margin-top:4px">Enter your till PIN to send. Messages are sent in your name.</div>' +
    '<input id="waPinInput" class="wa-pinin" type="password" inputmode="numeric" autocomplete="off" maxlength="8">' +
    '<div class="wa-err" id="waPinErr">' + esc(errMsg || '') + '</div>' +
    '<button class="wa-primary" id="waPinOk">Confirm</button>' +
    '<button class="wa-cancel" onclick="document.getElementById(\'waPinModal\').remove()">Cancel</button>' +
    // v125.47 (Matt: Bob sends, Steve arrives — how does Steve take over?).
    // The escape hatch lives IN the prompt: one tap to the who-picker, so the
    // next person never has to hunt for the small header switch.
    '<button class="wa-cancel" style="color:var(--chat-accent);font-weight:700" onclick="document.getElementById(\'waPinModal\').remove();Chat.switchWho()">Not <span translate="no">' + esc(_who ? _who.name : 'you') + '</span>? Switch person</button></div>';
  var shell = el.querySelector('.wa-shell') || el;
  shell.appendChild(wrap);
  var input = wrap.querySelector('#waPinInput');
  var ok = wrap.querySelector('#waPinOk');
  function accept() {
    var v = String(input.value || '').trim();
    if (v.length < 4) { wrap.querySelector('#waPinErr').textContent = 'Enter your PIN'; return; }
    _pin = v; _pinAt = Date.now();
    wrap.remove();
    if (typeof then === 'function') then();
  }
  ok.addEventListener('click', accept);
  input.addEventListener('keydown', function (e) { if (e.key === 'Enter') accept(); });
  setTimeout(function () { input.focus(); }, 50);
}

// ── 🔔 Notifications (A6-3): one-tap enable via the EXISTING push flow ─────────
function notifBannerHtml() {
  try {
    if (typeof Notification === 'undefined' || Notification.permission === 'granted') return '';
  } catch (e) { return ''; }
  return '<div class="wa-notice" style="cursor:pointer;background:var(--comms-note-bg);color:var(--warn-deep);font-weight:600" onclick="Chat.enableNotifs(this)">🔔 Turn on notifications — get a buzz when a message arrives</div>';
}
async function enableNotifs(el) {
  try {
    if (typeof Push === 'undefined') { if (typeof showToast !== 'undefined') showToast('Notifications module not loaded'); return; }
    if (isAccountMode()) await Push.enableManagers(el); else await Push.enableStaff(el);
    if (el && typeof Notification !== 'undefined' && Notification.permission === 'granted') el.remove();
  } catch (e) { /* the Push flow toasts its own errors */ }
}

// A6-3: ALWAYS-ON unread badge — a slow global poll keeps the nav badge honest
// even when you live on the Clock tab. 60s, only while the page is visible and
// a read identity exists. v125.58 (Matt: "preview of the message... if there
// are new messages like whatsapp") — this MUST include a store viewer (no name
// picked yet): read-without-pin (v125.51) reads by store, so gating on _who left
// the list frozen after the first load, so new-message previews never appeared.
if (typeof document !== 'undefined') {
  // v129.93 (request-volume audit): 60s → 120s. This global badge poll fires
  // 2 requests per identified device per tick all day, everywhere in the app
  // — a large share of the 8 Aug 87%-of-cap. The ACTIVE conversation
  // experience is untouched: the Chat screen's own 15s poll still runs while
  // the tab is open, so messages stay live; only the background badge
  // freshness moves from ≤1min to ≤2min.
  setInterval(function () {
    if (document.hidden) return;
    loadWho();
    if (isAccountMode() || readIdQ()) { refreshChannels(); refreshEmailUnread(); }   // v127.45: emails join the badge
  }, 120000);
}

// ── Polling: 15s while the Chat tab is open + visible ─────────────────────────
function startPolling() {
  if (_pollTimer) return;
  _pollTimer = setInterval(function () {
    if (!onChatScreen()) return;
    // v125.58: refresh for ANY read identity (account, picked name, OR the
    // signed-in store) — not only when a name is picked. Without this the
    // store-viewer chat list never updated with new messages.
    if (!isAccountMode() && !readIdQ()) return;
    if (_activeKey) { refreshThread(); refreshChannels(); }
    else if (!_pendingDm) refreshChannels();
  }, 15000);
  refreshChannels();
}

// ── Window bindings (classic-script contract; guarded so unit tests can import) ──
if (typeof window !== 'undefined') {
  window.renderChatScreen = renderChatScreen;
}

export {
  renderChatScreen, pickWho, switchWho, cancelWhoPick, openChannel, backToList,
  toggleEmoji, insertEmoji,
  openNewChat, startDm, send, esc, initials, fmtTime, dayLabel, renderMessages,
  photoPicked, videoPicked, clearPendingPhoto, viewPhoto, startEdit, cancelEdit, deleteMsg, enableNotifs,
  docPicked, clearPendingDoc, openMsgAttachment,      // v125.93 — documents in chat
  openEmailAttachment,                                // v125.95 — email attachments
  startForward, forwardTo, closeForward,              // v125.96 — WhatsApp-style forward
  startReply, cancelReply, toggleMute, toggleLike, quickReact,
  startCall,                                          // v129.35 — 📹 in the thread header
  startVoice, stopVoice, cancelVoice, clearPendingVoice, playVoice,   // v129.119 — 🎤 voice notes
  retryThread,                                                       // v129.121 — the stuck-"Loading…" escape hatch
  showReactions, closeReactions,                      // v127.49 — who-reacted sheet
  filterChats,                                        // v125.47 search box — unexported until v126.50
  commsTab, openJack, askJack,
  // Store Email PR2+PR3 — onclick handlers MUST be exported (the esbuild
  // globalName clobber lesson, v125.14): window.Chat IS the module exports.
  openEmail, emailBack, refreshEmails, loadOlderEmails, emailFolder, emailSort, jackMd,
  emailSearch,                                        // v129.106 — mail search box (Dubai Hills)
  composeEmail, closeCompose, sendEmailNow,
  emSpellingFix, emSpellingKeep,                      // v136.78: the compose screen's spelling check
  composeEmailForward, emCmd, emAttachPick, emAttachPicked, emAttachRemove,
  openAddressBook, filterAddressBook, pickAddress, pickGroup,   // v129.19 — department groups
};
